/**
 * `/calendar` page — monthly grid of bookable `slots`.
 *
 * Sits in the primary nav between Status and Data. Renders a 6-week month
 * grid; each day lists its slots as compact chips colour-coded by fill
 * (open / partially booked / full / inactive). Clicking a chip opens a
 * detail panel showing the trainer, time range, capacity, location, and the
 * members booked in — plus a dependency-aware "Delete slot" action that
 * reuses the same `/api/data-row/:entity` endpoint as the Data browser.
 *
 * Read surface over canonical `slots` (invariant 7 — no vendor-specific
 * components; queries the canonical model directly). ALL times render in the
 * STUDIO's timezone (`studio-tz`), never the browser's — a slot must read
 * identically from anywhere (browser-local rendering shifted every chip by
 * the viewer's offset when staff travelled), and the slot-create form
 * interprets its wall-clock the same way so entering "09:00" abroad can't
 * write a shifted instant.
 */

import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type PointerEvent as ReactPointerEvent,
  type ReactNode,
} from "react";
import { Link, useSearchParams } from "react-router-dom";
import {
  api,
  ApiError,
  dataRowDelete,
  dataRowDeleteMany,
  type CalendarBooking,
  type CalendarSlot,
  type DeleteDependent,
  type DeleteKept,
  type AvailabilityParseResponse,
  type SlotBulkParseResponse,
  type BulkRuleSummary,
  type BulkSlotOp,
} from "../lib/api";
import { dispatchAction } from "../lib/dispatch";
import {
  defaultIncluded,
  includeRule,
  needsNotifyChoice,
  planBulkApply,
  progressLabel,
  runBatchDelete,
} from "../lib/bulk-apply";
import { useSagaRun } from "../lib/use-saga-run";
import {
  studioTimeZone,
  isoToZonedDateKey,
  isoToZonedDatetimeInput,
  zonedDatetimeInputToIso,
} from "../lib/studio-tz";
import {
  describeChunkFailure,
  describeCoverage,
  judgeRotaOutcome,
  judgeSaveOutcome,
  planGenerateRanges,
  rotaPeriodFor,
  sumGeneration,
  type DateRange,
  type GenerationTotals,
  type RotaApplyResponse,
} from "../lib/availability-save-range";
// The timeline grid + the slot-presentation helpers now live beside the other
// shared components, so the member page's Book picker renders a day through the
// SAME code this page does. See `components/CalendarTimeline.tsx`.
import {
  CalendarTimeline,
  localDateKey,
  fmtTime,
  slotFill,
  slotLocation,
  slotChipName,
  slotChipShowsMember,
  slotChipTitle,
  slotStartMin,
  slotDurationMin,
} from "../components/CalendarTimeline";
import { isDoubleTap, type TapCandidate } from "../lib/double-tap";
import {
  cancellationDeadlineHours,
  isWithinCancellationWindow,
} from "../lib/studio-policy";
import { parseCalendarDeepLinkDate } from "../lib/calendar-deep-link";
import { CancelBookingModal } from "../components/CancelBookingModal";
import { PopMenu } from "../components/PopMenu";
import { PurchaseCreditsModal } from "../components/PurchaseCreditsModal";
import { useIsMobile, isMobileViewport } from "../lib/use-is-mobile";

interface MemberOption {
  readonly value: string;
  readonly label: string;
  readonly sublabel?: string;
}

/** book_session failure codes that mean "this member has no usable credit". */
const CREDIT_FAIL_RE =
  /insufficient_credits|credit_source_drained|package_slug_invalid/i;

const WEEKDAY_LABELS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];


/** Day-carrier Date (local midnight) for a `YYYY-MM-DD` key. */
function dayCarrierFromKey(key: string): Date {
  const [y, m, d] = key.split("-").map(Number);
  return new Date(y ?? 1970, (m ?? 1) - 1, d ?? 1);
}

/** Today as a day-carrier in the STUDIO's calendar (not the browser's). */
function studioToday(): Date {
  return dayCarrierFromKey(isoToZonedDateKey(new Date().toISOString()));
}

/** Monday-of-the-week on or before `d` (local midnight). */
function startOfWeekMonday(d: Date): Date {
  const out = new Date(d.getFullYear(), d.getMonth(), d.getDate());
  const dow = (out.getDay() + 6) % 7; // 0 = Monday
  out.setDate(out.getDate() - dow);
  return out;
}


function fmtMonthLabel(d: Date): string {
  return d.toLocaleDateString("en-GB", { month: "long", year: "numeric" });
}


/** The three Calendar layouts. Persisted in localStorage so it stays sticky. */
type CalView = "today" | "week" | "month";

const VIEW_STORAGE_KEY = "layer.calendar.view";

function loadStoredView(): CalView {
  try {
    const v = localStorage.getItem(VIEW_STORAGE_KEY);
    if (v === "today" || v === "week" || v === "month") return v;
  } catch {
    // localStorage unavailable (private mode / SSR) — fall through to default.
  }
  return "week";
}

/**
 * A `?view=` query param lets a link deep-link into a specific view,
 * overriding the stored preference. The mobile home screen uses
 * `/calendar?view=today` so phones land on the single-day view. `day`/`daily`
 * are accepted as friendly aliases for the internal `today` literal.
 */
function parseViewParam(raw: string | null): CalView | null {
  if (raw === "today" || raw === "day" || raw === "daily") return "today";
  if (raw === "week") return "week";
  if (raw === "month") return "month";
  return null;
}

/** Month view is hidden on phones — never resolve into it there. */
function coerceMobileView(v: CalView): CalView {
  return v === "month" && isMobileViewport() ? "week" : v;
}

/** Slice 10 — sticky Location + Trainer filters (client-side, per browser). */
type LocFilter = "all" | "studio" | "home";
/** Booked-only (2026-08-20) — the third sticky filter, same mechanism. */
type BookedFilter = "all" | "booked";
const LOC_FILTER_STORAGE_KEY = "layer.calendar.filter.location";
const TRAINER_FILTER_STORAGE_KEY = "layer.calendar.filter.trainer";
const BOOKED_FILTER_STORAGE_KEY = "layer.calendar.filter.booked";

function loadStoredLocFilter(): LocFilter {
  try {
    const v = localStorage.getItem(LOC_FILTER_STORAGE_KEY);
    if (v === "all" || v === "studio" || v === "home") return v;
  } catch {
    // localStorage unavailable — fall through to default.
  }
  return "all";
}

function loadStoredTrainerFilter(): string {
  try {
    return localStorage.getItem(TRAINER_FILTER_STORAGE_KEY) ?? "";
  } catch {
    return "";
  }
}

function loadStoredBookedFilter(): BookedFilter {
  try {
    if (localStorage.getItem(BOOKED_FILTER_STORAGE_KEY) === "booked") {
      return "booked";
    }
  } catch {
    // localStorage unavailable — fall through to default.
  }
  return "all";
}

/** Local midnight for `d` (drops the time-of-day). */
function startOfDay(d: Date): Date {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate());
}

function addDays(d: Date, n: number): Date {
  const out = startOfDay(d);
  out.setDate(out.getDate() + n);
  return out;
}

/** Add `n` calendar months, clamping the day-of-month to the target month. */
function addMonths(d: Date, n: number): Date {
  const first = new Date(d.getFullYear(), d.getMonth() + n, 1);
  const lastDay = new Date(
    first.getFullYear(),
    first.getMonth() + 1,
    0,
  ).getDate();
  first.setDate(Math.min(d.getDate(), lastDay));
  return first;
}

/** "Mon 23 Jun 2026" — the Today view header + week-cell tooltips. */
function fmtFullDay(d: Date): string {
  return d.toLocaleDateString("en-GB", {
    weekday: "short",
    day: "numeric",
    month: "short",
    year: "numeric",
  });
}

/** "23–29 Jun 2026" (collapses to one month/year when the week doesn't span). */
function fmtWeekRange(start: Date): string {
  const end = addDays(start, 6);
  const sameMonth =
    start.getMonth() === end.getMonth() &&
    start.getFullYear() === end.getFullYear();
  if (sameMonth) {
    return `${start.getDate()}–${end.getDate()} ${start.toLocaleDateString(
      "en-GB",
      { month: "short", year: "numeric" },
    )}`;
  }
  const sameYear = start.getFullYear() === end.getFullYear();
  const startStr = start.toLocaleDateString(
    "en-GB",
    sameYear
      ? { day: "numeric", month: "short" }
      : { day: "numeric", month: "short", year: "numeric" },
  );
  const endStr = end.toLocaleDateString("en-GB", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
  return `${startStr} – ${endStr}`;
}

interface DeleteState {
  readonly checking: boolean;
  readonly dependents: ReadonlyArray<DeleteDependent>;
  /** Rows the DB detaches and keeps on delete (cancelled bookings). */
  readonly kept: ReadonlyArray<DeleteKept>;
  readonly error: string | null;
  readonly deleting: boolean;
}

function SlotModal({
  slot,
  onClose,
  onChanged,
}: {
  slot: CalendarSlot;
  onClose: () => void;
  // Returns the parent's re-fetch promise so synchronous single-step writes
  // (hide / trainer / capacity) can hold their busy flag until the refreshed
  // slot actually lands and the button label flips with real data.
  onChanged: () => Promise<void> | void;
}): JSX.Element {
  const [del, setDel] = useState<DeleteState>({
    checking: false,
    dependents: [],
    kept: [],
    error: null,
    deleting: false,
  });
  const [confirming, setConfirming] = useState(false);
  const [hiding, setHiding] = useState(false);

  // Change-trainer state. The picker opens from the footer "Change trainer"
  // button; the trainer list is loaded once on open (small per-studio roster).
  const [changeTrainerOpen, setChangeTrainerOpen] = useState(false);
  const [trainerOptions, setTrainerOptions] =
    useState<ReadonlyArray<MemberOption> | null>(null);
  const [selectedTrainer, setSelectedTrainer] = useState("");
  const [changingTrainer, setChangingTrainer] = useState(false);
  // #13 — notify booked members their trainer changed (default ON). Only
  // meaningful when the slot has bookings; unchecking → silent reassignment.
  const [trainerNotify, setTrainerNotify] = useState(true);

  // Change-capacity state. The number input opens from the footer "Change
  // capacity" button and defaults to the slot's current capacity. The new
  // value cannot fall below `taken_count` (guarded client-side for feedback and
  // authoritatively by set_slot_capacity).
  const [changeCapacityOpen, setChangeCapacityOpen] = useState(false);
  const [capacityDraft, setCapacityDraft] = useState<number>(slot.capacity);
  const [changingCapacity, setChangingCapacity] = useState(false);

  // The booking whose Cancel was clicked — reveals the "Return credit" /
  // "Use credit" choice (staff override of the late-cancel policy). The choice
  // itself, its notify toggle and its saga poll live in `CancelBookingModal`,
  // which the member page renders too (C3) — one dispatch, two homes.
  const [cancelConfirmId, setCancelConfirmId] = useState<string | null>(null);
  // Mirrored up from that component so a cancel in flight still blocks "Book
  // in" on the same slot, exactly as it did when the poll lived here.
  const [cancelBusy, setCancelBusy] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const [bookOpen, setBookOpen] = useState(false);
  const [memberOptions, setMemberOptions] =
    useState<ReadonlyArray<MemberOption> | null>(null);
  const [memberQuery, setMemberQuery] = useState("");
  const [searchingMembers, setSearchingMembers] = useState(false);
  const [selectedMember, setSelectedMember] = useState("");
  // Remembered separately from `memberOptions` so the remedy header stays
  // correct even after a later search narrows the option list.
  const [selectedMemberName, setSelectedMemberName] = useState("");
  const [booking, setBooking] = useState(false);

  // book_session is an async saga (HTTP 202 + run_id). Poll the run to its
  // terminal state so we (a) refresh the slot only once the booking is really
  // applied, and (b) can detect an insufficient_credits failure. The cancel
  // path polls inside `CancelBookingModal`.
  const [pendingRunId, setPendingRunId] = useState<string | null>(null);
  const run = useSagaRun(pendingRunId);

  // No-credit remedy — shown when a booking fails for lack of credits. The
  // `PurchaseCreditsModal` component owns the sell/comp UI + its own catalog
  // loading; this flag just gates whether it's shown.
  const [noCredit, setNoCredit] = useState(false);

  // React when the polled book saga reaches a terminal state.
  useEffect(() => {
    if (pendingRunId === null || run.state === "in_progress") return;
    setPendingRunId(null);
    setBooking(false);
    if (run.state === "done") {
      setBookOpen(false);
      setNoCredit(false);
      onChanged();
      return;
    }
    if (run.state === "failed") {
      const err = run.last_error ?? "unknown error";
      if (CREDIT_FAIL_RE.test(err)) {
        setNoCredit(true);
        setActionError(null);
      } else {
        setActionError(`Booking failed: ${err}`);
      }
      return;
    }
    if (run.state === "forbidden") {
      // The action was dispatched and runs regardless — only the status
      // READ was refused. Do not call it a failure or a timeout.
      setActionError(
        `The action was dispatched, but this account was refused permission to check its progress (${run.last_error ?? "forbidden"}). Refresh to see the result.`,
      );
      return;
    }
    // timeout
    setActionError("Timed out waiting for the action — refresh to check.");
  }, [run.state, run.last_error, pendingRunId, onChanged]);

  const beginDelete = useCallback(async () => {
    setDel((p) => ({ ...p, checking: true, error: null }));
    try {
      const res = await dataRowDelete("slots", slot.id, "check");
      const deps = res.inspection?.dependents ?? [];
      setDel({
        checking: false,
        dependents: deps,
        kept: res.inspection?.kept ?? [],
        error: res.ok ? null : (res.code ?? "check_failed"),
        deleting: false,
      });
      setConfirming(true);
    } catch (e) {
      setDel({
        checking: false,
        dependents: [],
        kept: [],
        error: (e as Error).message,
        deleting: false,
      });
    }
  }, [slot.id]);

  // Hide / unhide the slot (set_slot_hidden — a single-step write, so the
  // dispatch returns the result synchronously; no run to poll). A hidden slot
  // stays active + staff-bookable and visible here, but drops off the
  // client-facing booking availability. onChanged re-fetches + re-points the
  // modal at the refreshed slot, flipping the button label.
  const toggleHidden = useCallback(async () => {
    setHiding(true);
    setActionError(null);
    try {
      const res = await dispatchAction<
        { slot_id: string; hidden: boolean },
        { hidden?: boolean }
      >("set_slot_hidden", { slot_id: slot.id, hidden: !slot.hidden });
      if (res.ok) {
        // Hold the busy flag through the re-fetch: the button stays disabled
        // until the modal is re-pointed at the refreshed slot, then flips to
        // the opposite label ready for an immediate undo.
        await onChanged();
        return;
      }
      setActionError(
        `${slot.hidden ? "Unhide" : "Hide"} failed: ${res.message || res.code}`,
      );
    } catch (e) {
      setActionError((e as Error).message);
    } finally {
      setHiding(false);
    }
  }, [slot.id, slot.hidden, onChanged]);

  const doDelete = useCallback(
    async (mode: "delete" | "cascade") => {
      setDel((p) => ({ ...p, deleting: true, error: null }));
      try {
        const res = await dataRowDelete("slots", slot.id, mode);
        if (res.ok || res.result?.ok) {
          onChanged();
          return;
        }
        setDel((p) => ({
          ...p,
          deleting: false,
          error: res.code ?? res.result?.code ?? "delete_failed",
        }));
      } catch (e) {
        setDel((p) => ({
          ...p,
          deleting: false,
          error: (e as Error).message,
        }));
      }
    },
    [slot.id, onChanged],
  );

  // Cancelling ONE booking is `CancelBookingModal` — see that file for the
  // `credit_handling` / `notify_member` wire. Closing the cluster and clearing
  // the mirrored busy flag are all that stay here.
  const closeCancel = useCallback(() => {
    setCancelConfirmId(null);
    setCancelBusy(false);
  }, []);

  // Load the studio's trainer roster once the change-trainer picker opens, and
  // default the dropdown to the slot's current trainer. The roster is small, so
  // a plain dropdown (no search) is enough.
  useEffect(() => {
    if (!changeTrainerOpen || trainerOptions !== null) return;
    let cancelled = false;
    void (async () => {
      try {
        const res = await api.lookupOptions("trainers");
        if (!cancelled) {
          setTrainerOptions(res.options);
          setSelectedTrainer(slot.trainer_id);
        }
      } catch (e) {
        if (!cancelled) {
          setActionError(`Couldn't load trainers: ${(e as Error).message}`);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [changeTrainerOpen, trainerOptions, slot.trainer_id]);

  // Reassign the slot to the chosen trainer (set_slot_trainer — single-step
  // write, returns synchronously). onChanged re-fetches + re-points the modal,
  // so the header + chips reflect the new trainer.
  const changeTrainer = useCallback(async () => {
    if (selectedTrainer === "" || selectedTrainer === slot.trainer_id) {
      setActionError("Pick a different trainer first.");
      return;
    }
    setChangingTrainer(true);
    setActionError(null);
    try {
      const res = await dispatchAction<
        { slot_id: string; trainer_id: string; notify_member?: boolean },
        { trainer_id?: string }
      >("set_slot_trainer", {
        slot_id: slot.id,
        trainer_id: selectedTrainer,
        // Only notify when the slot has booked members AND the operator left the
        // toggle on. The action's gate reads an explicit `true`.
        ...(slot.taken_count > 0 && trainerNotify ? { notify_member: true } : {}),
      });
      if (res.ok) {
        setChangeTrainerOpen(false);
        await onChanged();
        return;
      }
      setActionError(`Change trainer failed: ${res.message || res.code}`);
    } catch (e) {
      setActionError((e as Error).message);
    } finally {
      setChangingTrainer(false);
    }
  }, [
    slot.id,
    slot.trainer_id,
    slot.taken_count,
    selectedTrainer,
    trainerNotify,
    onChanged,
  ]);

  // Change the slot's capacity (set_slot_capacity — single-step write, returns
  // synchronously). Guarded client-side against dropping below taken_count; the
  // action re-validates authoritatively and returns capacity_below_taken_count.
  const changeCapacity = useCallback(async () => {
    if (!Number.isInteger(capacityDraft) || capacityDraft < 1) {
      setActionError("Enter a capacity of at least 1.");
      return;
    }
    if (capacityDraft < slot.taken_count) {
      setActionError(
        `Capacity can't be below the ${slot.taken_count} already booked.`,
      );
      return;
    }
    if (capacityDraft === slot.capacity) {
      setActionError("Pick a different capacity first.");
      return;
    }
    setChangingCapacity(true);
    setActionError(null);
    try {
      const res = await dispatchAction<
        { slot_id: string; capacity: number },
        { capacity?: number }
      >("set_slot_capacity", { slot_id: slot.id, capacity: capacityDraft });
      if (res.ok) {
        setChangeCapacityOpen(false);
        await onChanged();
        return;
      }
      setActionError(
        res.code === "capacity_below_taken_count"
          ? `Capacity can't be below the ${slot.taken_count} already booked.`
          : `Change capacity failed: ${res.message || res.code}`,
      );
    } catch (e) {
      setActionError((e as Error).message);
    } finally {
      setChangingCapacity(false);
    }
  }, [slot.id, slot.capacity, slot.taken_count, capacityDraft, onChanged]);

  // Open the book form. The member list is loaded by the debounced search
  // effect below (which fires on open with an empty query → newest members).
  const openBook = useCallback(() => {
    setBookOpen(true);
    setActionError(null);
  }, []);

  // Debounced server-side member search. The results list stays HIDDEN
  // until the operator starts typing; once there's a query the lookup
  // endpoint filters on display_name OR email OR phone (ILIKE) across ALL
  // members (not just the newest 50) and the matches appear.
  useEffect(() => {
    if (!bookOpen) return;
    const q = memberQuery.trim();
    if (q === "") {
      // Nothing typed → no suggestions shown.
      setMemberOptions(null);
      setSearchingMembers(false);
      return;
    }
    let cancelled = false;
    setSearchingMembers(true);
    const handle = setTimeout(() => {
      void (async () => {
        try {
          const res = await api.lookupOptions("members", q);
          if (!cancelled) setMemberOptions(res.options);
        } catch (e) {
          if (!cancelled) {
            setActionError(`Couldn't load members: ${(e as Error).message}`);
          }
        } finally {
          if (!cancelled) setSearchingMembers(false);
        }
      })();
    }, 220);
    return () => {
      cancelled = true;
      clearTimeout(handle);
    };
  }, [memberQuery, bookOpen]);

  // Book the chosen member into THIS slot (book_session saga). Leaving the
  // package slug blank lets the saga draw on the member's existing active
  // credit. Poll the run; an insufficient_credits failure flips to the remedy.
  const submitBook = useCallback(async () => {
    if (selectedMember === "") {
      setActionError("Pick a member first.");
      return;
    }
    setBooking(true);
    setActionError(null);
    setNoCredit(false);
    try {
      // Pre-flight credit check. A book_session for a member with no usable
      // credit fails with `insufficient_credits`, but the saga can take ~2-3
      // minutes to reach that terminal state — longer than the run-poll window
      // — so it just looks "stuck at Working…". Detect a definitive zero up
      // front and jump straight to the remedy WITHOUT dispatching a doomed
      // saga. Only short-circuit when BOTH reads succeed and find nothing;
      // otherwise fall through and let book_session be authoritative.
      {
        const [pkgRes, memRes] = await Promise.all([
          dispatchAction<
            Record<string, unknown>,
            { member_packages?: ReadonlyArray<{ remaining_sessions?: number }> }
          >("list_member_packages", {
            member_id: selectedMember,
            status: "active",
          }),
          dispatchAction<
            Record<string, unknown>,
            { member_memberships?: ReadonlyArray<unknown> }
          >("list_member_memberships", {
            member_id: selectedMember,
            status: "active",
          }),
        ]);
        const hasPkgCredit =
          pkgRes.ok &&
          (pkgRes.response.member_packages ?? []).some(
            (p) => (p.remaining_sessions ?? 0) > 0,
          );
        const hasMembership =
          memRes.ok && (memRes.response.member_memberships ?? []).length > 0;
        if (pkgRes.ok && memRes.ok && !hasPkgCredit && !hasMembership) {
          setBooking(false);
          setNoCredit(true);
          return;
        }
      }
      // Always draw on the member's existing active credit (the saga
      // auto-selects the credit source). No per-booking package override.
      const input: Record<string, unknown> = {
        slot_id: slot.id,
        member_id: selectedMember,
      };
      const res = await dispatchAction<
        Record<string, unknown>,
        { run_id?: string }
      >("book_session", input);
      if (res.ok) {
        const rid = res.response?.run_id;
        if (typeof rid === "string") {
          setPendingRunId(rid);
        } else {
          setBooking(false);
          onChanged();
        }
        return;
      }
      setBooking(false);
      if (CREDIT_FAIL_RE.test(`${res.code} ${res.message}`)) {
        setNoCredit(true);
      } else {
        setActionError(`Booking failed: ${res.message || res.code}`);
      }
    } catch (e) {
      setBooking(false);
      setActionError((e as Error).message);
    }
  }, [slot.id, selectedMember, onChanged]);


  const hasDependents = del.dependents.length > 0;
  const hasSpace = slot.status === "active" && slot.taken_count < slot.capacity;
  const sagaBusy = pendingRunId !== null;

  // Commit a member pick from the native `<select size>` listbox. Wired to
  // BOTH `onChange` (keyboard / changed-row clicks) AND `onClick` (any row,
  // including the FIRST one the browser auto-highlights): a controlled
  // `value=""` matches no option, so the browser pre-highlights row 0 without
  // it being the React-selected value — clicking that already-highlighted top
  // row fires no `change` event, leaving `selectedMember` empty and "Book in"
  // disabled until you bounce off another row. The `onClick` capture makes the
  // most-recently-added (top) member bookable on the first click.
  const selectMember = (value: string): void => {
    if (value === "") return;
    setSelectedMember(value);
    setSelectedMemberName(
      (memberOptions ?? []).find((o) => o.value === value)?.label ?? "",
    );
    setNoCredit(false);
  };
  const selectedMemberLabel =
    selectedMemberName.length > 0
      ? selectedMemberName
      : ((memberOptions ?? []).find((o) => o.value === selectedMember)?.label ??
        "this member");

  return (
    <div className="cal-modal-backdrop" onClick={onClose}>
      <div className="cal-modal" onClick={(e) => e.stopPropagation()}>
        <div className="cal-modal-head">
          <h3>{slot.trainer_name ?? "Unassigned trainer"}</h3>
          <button className="cal-modal-x" onClick={onClose} aria-label="Close">
            ×
          </button>
        </div>
        <div className="cal-modal-body">
          <dl className="cal-modal-dl">
            <dt>When</dt>
            <dd>
              {new Date(slot.starts_at).toLocaleDateString("en-GB", {
                timeZone: studioTimeZone(),
                weekday: "short",
                day: "2-digit",
                month: "short",
              })}{" "}
              · {fmtTime(slot.starts_at)}–{fmtTime(slot.ends_at)}
            </dd>
            <dt>Status</dt>
            <dd>
              <span className={`tag cal-tag-${slotFill(slot)}`}>
                {slot.status}
              </span>
              {slot.hidden ? (
                <span
                  className="tag cal-tag-hidden"
                  title="Active but hidden from client booking"
                >
                  hidden
                </span>
              ) : null}
            </dd>
            <dt>Capacity</dt>
            <dd>
              {slot.taken_count} / {slot.capacity} booked
            </dd>
            {slot.location !== null && slot.location.length > 0 ? (
              <>
                <dt>Location</dt>
                <dd>{slot.location}</dd>
              </>
            ) : null}
          </dl>

          <h4 className="cal-modal-sub">Booked members</h4>
          {slot.bookings.length === 0 ? (
            <p className="cal-modal-empty">No one booked in yet.</p>
          ) : (
            <ul className="cal-modal-bookings">
              {slot.bookings.map((b) => (
                <li key={b.id}>
                  {b.member_id != null ? (
                    <Link
                      to={`/members/${encodeURIComponent(b.member_id)}`}
                      className="cal-booking-name data-member-link"
                      onClick={(e) => e.stopPropagation()}
                    >
                      {b.member_name ?? "Member"}
                    </Link>
                  ) : (
                    <span className="cal-booking-name">
                      {b.member_name ?? "Unknown member"}
                    </span>
                  )}
                  <span className="cal-booking-right">
                    <span
                      className={`tag tag-${b.status === "completed" ? "success" : "info"}`}
                    >
                      {b.status}
                    </span>
                    {b.status === "active" ? (
                      cancelConfirmId === b.id ? (
                        <CancelBookingModal
                          bookingId={b.id}
                          startsAt={slot.starts_at}
                          disabled={sagaBusy}
                          onBusyChange={setCancelBusy}
                          onDone={() => {
                            closeCancel();
                            onChanged();
                          }}
                          onClose={closeCancel}
                        />
                      ) : (
                        <button
                          className="cal-cancel-btn"
                          disabled={sagaBusy || cancelBusy}
                          onClick={() => {
                            setCancelConfirmId(b.id);
                            setActionError(null);
                          }}
                        >
                          Cancel
                        </button>
                      )
                    ) : null}
                  </span>
                </li>
              ))}
            </ul>
          )}

          {/* Book someone in — shown while the slot has free capacity. */}
          {hasSpace ? (
            bookOpen ? (
              <div className="cal-book-form">
                <input
                  type="text"
                  className="cal-book-search"
                  placeholder="Search members by name, email, or phone…"
                  value={memberQuery}
                  onChange={(e) => setMemberQuery(e.target.value)}
                />
                {/* Results list stays hidden until the operator types. */}
                {memberQuery.trim().length > 0 ? (
                  <select
                    className="cal-book-select"
                    value={selectedMember}
                    onChange={(e) => selectMember(e.target.value)}
                    onClick={(e) => {
                      // Native listbox: a click on a row targets the <option>.
                      // Commit it directly so the auto-highlighted first row
                      // (which fires no change event) still selects on click 1.
                      const t = e.target;
                      if (t instanceof HTMLOptionElement) {
                        selectMember(t.value);
                      }
                    }}
                    size={6}
                  >
                    {memberOptions === null || searchingMembers ? (
                      <option value="">Searching…</option>
                    ) : memberOptions.length === 0 ? (
                      <option value="">No matches</option>
                    ) : (
                      memberOptions.map((o) => (
                        <option key={o.value} value={o.value}>
                          {o.label}
                          {o.sublabel !== undefined ? ` · ${o.sublabel}` : ""}
                        </option>
                      ))
                    )}
                  </select>
                ) : null}
                <div className="cal-del-actions">
                  <button
                    className="cal-book-confirm"
                    disabled={
                      booking || sagaBusy || cancelBusy || selectedMember === ""
                    }
                    onClick={() => void submitBook()}
                  >
                    {booking || sagaBusy ? "Booking…" : "Book in"}
                  </button>
                  <button onClick={() => setBookOpen(false)}>Close</button>
                </div>

                {/* No-credit remedy — shown when book_session fails for lack of
                    credits. Sell a package (Stripe link) or grant one free. */}
                {noCredit ? (
                  <PurchaseCreditsModal
                    memberId={selectedMember}
                    headline={`${selectedMemberLabel} has no available credits for this session. Sell or comp a package or membership:`}
                    autoBook={{
                      slotId: slot.id,
                      onBookStarted: (rid) => {
                        setNoCredit(false);
                        setBooking(true);
                        setPendingRunId(rid);
                      },
                      onBookedSync: () => {
                        setNoCredit(false);
                        onChanged();
                      },
                    }}
                  />
                ) : null}
              </div>
            ) : (
              <button
                className="cal-book-open-btn"
                onClick={() => void openBook()}
              >
                + Book someone in
              </button>
            )
          ) : null}

          {/* Change trainer — reassigns the slot (and any bookings on it) to a
              different trainer. Shown whether or not anyone is booked in. */}
          {changeTrainerOpen ? (
            <div className="cal-changetrainer-form">
              <label className="cal-changetrainer-label" htmlFor="cal-trainer-select">
                Move this session to
              </label>
              <select
                id="cal-trainer-select"
                className="cal-book-select"
                value={selectedTrainer}
                onChange={(e) => setSelectedTrainer(e.target.value)}
                disabled={trainerOptions === null}
              >
                {trainerOptions === null ? (
                  <option value="">Loading trainers…</option>
                ) : (
                  trainerOptions.map((o) => (
                    <option key={o.value} value={o.value}>
                      {o.label}
                      {o.value === slot.trainer_id ? " (current)" : ""}
                    </option>
                  ))
                )}
              </select>
              {slot.taken_count > 0 ? (
                <label
                  className="cal-retime-notify"
                  title="Message the booked member(s) that their trainer changed"
                >
                  <input
                    type="checkbox"
                    checked={trainerNotify}
                    disabled={changingTrainer}
                    onChange={(e) => setTrainerNotify(e.target.checked)}
                  />{" "}
                  Notify the booked member{slot.taken_count === 1 ? "" : "s"}
                </label>
              ) : null}
              <div className="cal-del-actions">
                <button
                  className="cal-book-confirm"
                  disabled={
                    changingTrainer ||
                    trainerOptions === null ||
                    selectedTrainer === "" ||
                    selectedTrainer === slot.trainer_id
                  }
                  onClick={() => void changeTrainer()}
                >
                  {changingTrainer ? "Saving…" : "Save trainer"}
                </button>
                <button
                  disabled={changingTrainer}
                  onClick={() => setChangeTrainerOpen(false)}
                >
                  Close
                </button>
              </div>
            </div>
          ) : null}

          {/* Change capacity — resize the slot. The new value cannot fall
              below taken_count (the number already booked in). */}
          {changeCapacityOpen ? (
            <div className="cal-changetrainer-form">
              <label
                className="cal-changetrainer-label"
                htmlFor="cal-capacity-input"
              >
                Capacity (currently {slot.capacity} · {slot.taken_count} booked)
              </label>
              <input
                id="cal-capacity-input"
                className="cal-book-select"
                type="number"
                min={Math.max(1, slot.taken_count)}
                max={50}
                value={capacityDraft}
                onChange={(e) => setCapacityDraft(Number(e.target.value))}
              />
              <div className="cal-del-actions">
                <button
                  className="cal-book-confirm"
                  disabled={
                    changingCapacity ||
                    capacityDraft === slot.capacity ||
                    capacityDraft < slot.taken_count ||
                    capacityDraft < 1
                  }
                  onClick={() => void changeCapacity()}
                >
                  {changingCapacity ? "Saving…" : "Save capacity"}
                </button>
                <button
                  disabled={changingCapacity}
                  onClick={() => setChangeCapacityOpen(false)}
                >
                  Close
                </button>
              </div>
            </div>
          ) : null}

          {sagaBusy || cancelBusy ? (
            <p className="cal-saga-busy">
              Working… {sagaBusy ? "booking" : "cancelling"} in progress.
            </p>
          ) : null}

          {actionError !== null ? (
            <p className="cal-del-error cal-action-error">{actionError}</p>
          ) : null}
        </div>

        <div className="cal-modal-foot">
          {!confirming ? (
            <div className="cal-modal-foot-actions">
              <button
                className="cal-hide-btn"
                disabled={hiding}
                title={
                  slot.hidden
                    ? "Show this slot on the client booking surface again"
                    : "Keep this slot active but hide it from client booking"
                }
                onClick={() => void toggleHidden()}
              >
                {hiding
                  ? slot.hidden
                    ? "Unhiding…"
                    : "Hiding…"
                  : slot.hidden
                    ? "Unhide slot"
                    : "Hide slot"}
              </button>
              <button
                className="cal-changetrainer-btn"
                title="Reassign this slot (and any bookings on it) to a different trainer"
                onClick={() => {
                  setChangeTrainerOpen((v) => !v);
                  setTrainerNotify(true);
                  setActionError(null);
                }}
              >
                Change trainer
              </button>
              <button
                className="cal-changetrainer-btn"
                title="Change how many members can be booked into this slot"
                onClick={() => {
                  setChangeCapacityOpen((v) => !v);
                  setCapacityDraft(slot.capacity);
                  setActionError(null);
                }}
              >
                Change capacity
              </button>
              <button
                className="cal-btn-danger"
                disabled={del.checking}
                onClick={() => void beginDelete()}
              >
                {del.checking ? "Checking…" : "Delete slot"}
              </button>
            </div>
          ) : (
            <div className="cal-del-confirm">
              {del.error !== null ? (
                <p className="cal-del-error">Error: {del.error}</p>
              ) : null}
              {hasDependents ? (
                <>
                  <p>
                    This slot has dependent rows:{" "}
                    {del.dependents
                      .map((d) => `${d.count} ${d.table}`)
                      .join(", ")}
                    . Deleting it will also remove them.
                  </p>
                  {del.kept.length > 0 ? (
                    <p>
                      Kept:{" "}
                      {del.kept
                        .map((k) => `${k.count} ${k.label}`)
                        .join("; ")}
                      .
                    </p>
                  ) : null}
                  <div className="cal-del-actions">
                    <button
                      className="cal-btn-danger"
                      disabled={del.deleting}
                      onClick={() => void doDelete("cascade")}
                    >
                      {del.deleting ? "Deleting…" : "Delete slot + dependents"}
                    </button>
                    <button onClick={() => setConfirming(false)}>Cancel</button>
                  </div>
                </>
              ) : (
                <>
                  <p>Delete this slot? This cannot be undone.</p>
                  {del.kept.length > 0 ? (
                    <p>
                      Kept:{" "}
                      {del.kept
                        .map((k) => `${k.count} ${k.label}`)
                        .join("; ")}
                      .
                    </p>
                  ) : null}
                  <div className="cal-del-actions">
                    <button
                      className="cal-btn-danger"
                      disabled={del.deleting}
                      onClick={() => void doDelete("delete")}
                    >
                      {del.deleting ? "Deleting…" : "Confirm delete"}
                    </button>
                    <button onClick={() => setConfirming(false)}>Cancel</button>
                  </div>
                </>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

const WEEKDAY_SHORT = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

/**
 * Human wording for a failed natural-language parse (both Calendar tools).
 * Keyed on the route's stable code — until 2026-09-02 a timeout rendered as
 * the raw `Parse failed: api_failed:504:anthropic_timeout`, which told the
 * operator nothing about what to do next (split the paste).
 */
function describeParseFailure(e: unknown, tool: "availability" | "bulk"): string {
  const code = e instanceof ApiError ? e.code : null;
  const noun = tool === "availability" ? "availability" : "bulk-edit";
  switch (code) {
    case "anthropic_api_key_missing":
      return `The ${noun} parser isn't configured on this service yet (no Anthropic key).`;
    case "anthropic_timeout":
      return tool === "availability"
        ? "That took too long to read — the model ran out of time. Paste one trainer at a time, or fewer days, and try again."
        : "That took too long to read — the model ran out of time. Narrow the instruction (one day or one trainer) and try again.";
    case "availability_parse_truncated":
    case "slot_bulk_parse_truncated":
      return tool === "availability"
        ? "Too much for one read — the proposal was cut off before the end, so nothing is shown. Split the paste (one trainer at a time) and try again."
        : "Too much for one read — the rules were cut off, so nothing is shown. Shorten the instruction and try again.";
    case "slot_bulk_parse_malformed":
      return "Couldn't turn that into rules. Name a day or a date range, e.g. 'delete Mario's empty slots in January 2027'.";
    case "availability_period_required":
      return "This studio schedules by dates, and no period was found for at least one trainer. Say which dates — e.g. 'for October' or '4–31 January' — and parse again.";
    case "availability_parse_no_tool_call":
      return "Couldn't turn that into a schedule. Try the form 'Name: Mon–Fri 7am–2pm, studio'.";
    case "slot_bulk_parse_no_tool_call":
      return "Couldn't turn that into slot changes. Name a day, time or trainer — e.g. 'hide Nathalia's Friday 9am'.";
    case "availability_text_too_long":
      return "That paste is over the 8,000-character limit — split it by trainer.";
    case "slot_bulk_text_too_long":
      return "That instruction is over the 4,000-character limit — shorten it.";
    case "slot_bulk_no_candidates":
      return "There are no slots in the current view to edit — widen the date range first.";
    default:
      return `Parse failed: ${(e as Error).message}`;
  }
}

/**
 * Copy for a Save-step dispatch failure the operator can act on. The action's
 * own message is the fallback — it is authoritative, just not friendly.
 */
function describeSaveFailure(code: string, message: string): string {
  if (code === "availability_period_required") {
    return "This studio schedules by dates — every window needs a period (e.g. 'for October'). Nothing was saved for this trainer; add the dates and parse again.";
  }
  return message || code;
}

/** Studio-calendar `YYYY-MM-DD` for today + `days`. */
function todayPlusDays(days: number): string {
  const d = studioToday();
  d.setDate(d.getDate() + days);
  return localDateKey(d);
}

interface SaveOutcome {
  readonly trainer_name: string;
  readonly ok: boolean;
  readonly windows_set?: number | undefined;
  readonly slots_created?: number | undefined;
  /** The dates the slots were generated for — taken from the windows themselves. */
  readonly coverage?: string | undefined;
  /** A caveat on an OK outcome (e.g. the slots already existed). */
  readonly note?: string | undefined;
  readonly error?: string | undefined;
}

/**
 * "Add availability" tool — paste free text → LLM parses it into a structured
 * proposal (preview) → Save dispatches ONE `upload_trainer_rota` (period form)
 * per trainer when every window is dated — windows replaced for the period,
 * slots reconciled — else `set_trainer_availability` + `generate_slots` per
 * range, all through the normal chokepoint. Preview-then-save:
 * nothing is written until the operator confirms what was understood.
 */
function AvailabilityModal({
  onClose,
  onSaved,
}: {
  onClose: () => void;
  onSaved: () => void;
}): JSX.Element {
  const [text, setText] = useState("");
  const [parsing, setParsing] = useState(false);
  const [parseError, setParseError] = useState<string | null>(null);
  const [result, setResult] = useState<AvailabilityParseResponse | null>(null);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [outcomes, setOutcomes] = useState<ReadonlyArray<SaveOutcome> | null>(
    null,
  );
  // 'append' adds to the trainer's existing schedule (default for temporary
  // bounded blocks); 'replace' swaps their whole schedule (default for a full
  // permanent paste). The operator can flip it before saving.
  const [applyMode, setApplyMode] = useState<"replace" | "append">("replace");
  // Whether every proposed window is dated — the period-scoped reconcile path.
  const allBoundedResult =
    result !== null &&
    result.proposals.every((p) =>
      p.windows.every(
        (w) => w.effective_from !== null && w.effective_until !== null,
      ),
    );

  const parse = useCallback(async () => {
    setParsing(true);
    setParseError(null);
    setOutcomes(null);
    try {
      const r = await api.parseAvailability(text);
      setResult(r);
      const anyBounded = r.proposals.some((p) =>
        p.windows.some(
          (w) => w.effective_from !== null || w.effective_until !== null,
        ),
      );
      const allBounded = r.proposals.every((p) =>
        p.windows.every(
          (w) => w.effective_from !== null && w.effective_until !== null,
        ),
      );
      // A fully dated paste replaces ONLY its own dates (period-scoped, via
      // upload_trainer_rota), so "replace" is the safe default there; a paste
      // that mixes in a permanent window goes through the whole-schedule
      // action, where "append" protects the standing schedule.
      setApplyMode(allBounded ? "replace" : anyBounded ? "append" : "replace");
      if (r.proposals.length === 0) {
        setParseError(
          r.unmatched_names.length > 0
            ? `No active trainer matched: ${r.unmatched_names.join(", ")}. Use the names from your roster.`
            : "Couldn't find any availability in that text. Try e.g. 'Nathalia Mon–Fri 7am–2pm studio'.",
        );
      }
    } catch (e) {
      // A failed parse must not leave the PREVIOUS preview saveable beneath
      // the error — Save would then write the old paste, not this one.
      setResult(null);
      setParseError(describeParseFailure(e, "availability"));
    } finally {
      setParsing(false);
    }
  }, [text]);

  const save = useCallback(async () => {
    if (result === null) return;
    setSaving(true);
    setSaveError(null);
    const today = todayPlusDays(0);
    const results: SaveOutcome[] = [];
    for (const p of result.proposals) {
      const windows = p.windows.map((w) => ({
        weekday: w.weekday,
        start_time: w.start_time,
        end_time: w.end_time,
        ...(w.location !== null ? { location: w.location } : {}),
        ...(w.session_type !== null ? { session_type: w.session_type } : {}),
        ...(w.capacity !== null ? { capacity: w.capacity } : {}),
        ...(w.effective_from !== null ? { effective_from: w.effective_from } : {}),
        ...(w.effective_until !== null ? { effective_until: w.effective_until } : {}),
      }));
      const allBounded = p.windows.every(
        (w) => w.effective_from !== null && w.effective_until !== null,
      );
      if (allBounded) {
        // A dated paste is ONE reconcile: the pasted windows become the
        // trainer's schedule for the period they cover, stale unbooked slots
        // in that period are pruned, booked ones outside the new schedule are
        // reported — never a second paste surfacing Wednesdays nobody asked
        // for (2026-09-02).
        const period = rotaPeriodFor(p.windows);
        try {
          const rotaRes = await dispatchAction<
            Record<string, unknown>,
            RotaApplyResponse
          >("upload_trainer_rota", {
            trainer_id: p.trainer_id,
            period: { from: period.from_date, until: period.to_date },
            mode: applyMode,
            windows,
          });
          if (!rotaRes.ok) {
            results.push({
              trainer_name: p.trainer_name,
              ok: false,
              error: describeSaveFailure(rotaRes.code, rotaRes.message),
            });
            continue;
          }
          const verdict = judgeRotaOutcome({ period, response: rotaRes.response });
          const created = rotaRes.response?.slots_created ?? 0;
          results.push(
            verdict.ok
              ? {
                  trainer_name: p.trainer_name,
                  ok: true,
                  windows_set: rotaRes.response?.windows_set ?? 0,
                  slots_created: created,
                  coverage: describeCoverage([period]),
                  ...(verdict.note !== null ? { note: verdict.note } : {}),
                }
              : {
                  trainer_name: p.trainer_name,
                  ok: false,
                  windows_set: rotaRes.response?.windows_set ?? 0,
                  slots_created: created,
                  error: verdict.reason,
                },
          );
        } catch (e) {
          results.push({
            trainer_name: p.trainer_name,
            ok: false,
            error: (e as Error).message,
          });
        }
        continue;
      }
      try {
        const setRes = await dispatchAction<
          Record<string, unknown>,
          { windows_set?: number }
        >("set_trainer_availability", {
          trainer_id: p.trainer_id,
          windows,
          apply_mode: applyMode,
        });
        if (!setRes.ok) {
          results.push({
            trainer_name: p.trainer_name,
            ok: false,
            error: describeSaveFailure(setRes.code, setRes.message),
          });
          continue;
        }
        // Generate for the dates the windows ACTUALLY cover: a bounded block for
        // its own dates (chunked under the action's span cap), a permanent one
        // for the rolling horizon. Never a fixed range — that is how a January
        // paste saved its windows and produced zero slots (2026-09-02).
        const windowsSet = setRes.response?.windows_set ?? 0;
        const ranges = planGenerateRanges(p.windows, today);
        const parts: GenerationTotals[] = [];
        const done: DateRange[] = [];
        const failed: { range: DateRange; message: string }[] = [];
        for (const range of ranges) {
          const genRes = await dispatchAction<
            Record<string, unknown>,
            {
              slots_created?: number;
              slots_skipped?: number;
              windows_expanded?: number;
            }
          >("generate_slots", {
            trainer_id: p.trainer_id,
            from_date: range.from_date,
            to_date: range.to_date,
          });
          if (!genRes.ok) {
            // Keep going: a rejected chunk must not silently skip the rest, and
            // the line below names exactly which dates have no slots.
            failed.push({ range, message: genRes.message || genRes.code });
            continue;
          }
          done.push(range);
          parts.push({
            slots_created: genRes.response?.slots_created ?? 0,
            slots_skipped: genRes.response?.slots_skipped ?? 0,
            windows_expanded: genRes.response?.windows_expanded ?? 0,
          });
        }
        const totals = sumGeneration(parts);
        if (failed.length > 0) {
          results.push({
            trainer_name: p.trainer_name,
            ok: false,
            windows_set: windowsSet,
            slots_created: totals.slots_created,
            error: describeChunkFailure({
              windows_set: windowsSet,
              done,
              failed,
              totals,
            }),
          });
          continue;
        }
        const verdict = judgeSaveOutcome({
          windows_set: windowsSet,
          ranges,
          totals,
        });
        results.push(
          verdict.ok
            ? {
                trainer_name: p.trainer_name,
                ok: true,
                windows_set: windowsSet,
                slots_created: totals.slots_created,
                coverage: describeCoverage(ranges),
                ...(verdict.note !== null ? { note: verdict.note } : {}),
              }
            : {
                trainer_name: p.trainer_name,
                ok: false,
                windows_set: windowsSet,
                slots_created: totals.slots_created,
                error: verdict.reason,
              },
        );
      } catch (e) {
        results.push({
          trainer_name: p.trainer_name,
          ok: false,
          error: (e as Error).message,
        });
      }
    }
    setOutcomes(results);
    setSaving(false);
    if (results.some((r) => r.ok || (r.slots_created ?? 0) > 0)) {
      // A reconcile can change the grid without creating anything (pruned
      // slots), so any OK outcome reloads it.
      onSaved();
    }
    if (!results.every((r) => r.ok)) {
      setSaveError("Some trainers didn't save — see below.");
    }
  }, [result, onSaved, applyMode]);

  return (
    <div className="cal-modal-backdrop" onClick={onClose}>
      <div className="cal-modal" onClick={(e) => e.stopPropagation()}>
        <div className="cal-modal-head">
          <h3>Add availability</h3>
          <button className="cal-modal-x" onClick={onClose} aria-label="Close">
            ×
          </button>
        </div>
        <div className="cal-modal-body">
          {outcomes !== null ? (
            <div className="cal-avail-done">
              <h4 className="cal-modal-sub">
                {outcomes.every((o) => o.ok) ? "Saved" : "Not saved as asked"}
              </h4>
              <ul className="cal-avail-windows">
                {outcomes.map((o, i) => (
                  <li
                    key={i}
                    className={o.ok ? undefined : "cal-avail-outcome-bad"}
                  >
                    {o.ok ? "✓" : "✗"} <strong>{o.trainer_name}</strong>
                    {o.ok
                      ? ` — ${o.windows_set ?? 0} windows, ${o.slots_created ?? 0} slots generated for ${o.coverage ?? "the coming weeks"}${o.note !== undefined ? ` (${o.note})` : ""}`
                      : ` — ${o.error}`}
                  </li>
                ))}
              </ul>
              <div className="cal-del-actions">
                <button className="cal-book-confirm" onClick={onClose}>
                  Done
                </button>
              </div>
            </div>
          ) : (
            <>
              <p className="cal-modal-empty">
                Paste each trainer's weekly availability in plain English — e.g.{" "}
                <em>
                  "Nathalia: Mon–Fri 7am–2pm studio. Michael: Tue &amp; Thu 9–5
                  plus Sat mornings."
                </em>{" "}
                You'll see exactly what it understood before anything saves.
                One trainer per paste reads fastest.
              </p>
              <textarea
                className="cal-avail-text"
                rows={6}
                maxLength={8000}
                placeholder="Nathalia: Mon to Fri 7am–2pm, studio…"
                value={text}
                onChange={(e) => setText(e.target.value)}
              />
              <div className="cal-del-actions">
                <button
                  className="cal-book-confirm"
                  disabled={parsing || text.trim().length === 0}
                  onClick={() => void parse()}
                >
                  {parsing
                    ? "Reading…"
                    : result === null
                      ? "Parse"
                      : "Re-parse"}
                </button>
              </div>
              {parseError !== null ? (
                <p className="cal-del-error cal-action-error">{parseError}</p>
              ) : null}

              {result !== null && result.proposals.length > 0 ? (
                <div className="cal-avail-preview">
                  <h4 className="cal-modal-sub">Here's what I understood</h4>
                  {result.proposals.map((p) => (
                    <div key={p.trainer_id} className="cal-avail-trainer">
                      <strong>{p.trainer_name}</strong>
                      <ul className="cal-avail-windows">
                        {p.windows.map((w, i) => (
                          <li key={i}>
                            {WEEKDAY_SHORT[w.weekday]} {w.start_time}–{w.end_time}
                            {w.session_type !== null ? ` · ${w.session_type}` : ""}
                            {w.location !== null && w.location !== w.session_type
                              ? ` · ${w.location}`
                              : ""}
                            {w.bound_label !== null ? (
                              <span className="cal-avail-bound"> · {w.bound_label}</span>
                            ) : null}
                          </li>
                        ))}
                      </ul>
                    </div>
                  ))}
                  {result.unmatched_names.length > 0 ? (
                    <p className="cal-del-error">
                      Not matched to a trainer (skipped):{" "}
                      {result.unmatched_names.join(", ")}
                    </p>
                  ) : null}
                  {result.notes !== null ? (
                    <p className="cal-avail-notes">Note: {result.notes}</p>
                  ) : null}
                  <div className="cal-avail-mode">
                    <label className="cal-avail-mode-opt">
                      <input
                        type="radio"
                        name="apply-mode"
                        checked={applyMode === "append"}
                        onChange={() => setApplyMode("append")}
                      />
                      <span>
                        <strong>Add to existing</strong> —{" "}
                        {allBoundedResult
                          ? "keep the trainer's other windows and slots for these dates, add these alongside"
                          : "keep their current schedule, add these"}
                      </span>
                    </label>
                    <label className="cal-avail-mode-opt">
                      <input
                        type="radio"
                        name="apply-mode"
                        checked={applyMode === "replace"}
                        onChange={() => setApplyMode("replace")}
                      />
                      <span>
                        <strong>{allBoundedResult ? "Replace these dates" : "Replace schedule"}</strong>{" "}
                        —{" "}
                        {allBoundedResult
                          ? "these become the trainer's schedule for the dates pasted: their other windows for those dates go, unbooked slots that no longer fit are removed, booked ones are kept and listed"
                          : "these become the trainer's whole schedule"}
                      </span>
                    </label>
                  </div>
                  <div className="cal-del-actions">
                    <button
                      className="cal-book-confirm"
                      disabled={saving}
                      onClick={() => void save()}
                    >
                      {saving
                        ? "Saving…"
                        : applyMode === "append"
                          ? "Add availability"
                          : "Replace & save"}
                    </button>
                  </div>
                  {saveError !== null ? (
                    <p className="cal-del-error cal-action-error">{saveError}</p>
                  ) : null}
                </div>
              ) : null}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

/** One row's dispatch result in the bulk-apply pass. */
interface BulkApplyOutcome {
  readonly slot_id: string;
  readonly summary: string;
  readonly ok: boolean;
  readonly error?: string;
}

/**
 * "Bulk edit / delete" tool — parse a free-text calendar instruction into a
 * batch of per-slot ops, preview them (grounded on the current window's visible
 * slots), then dispatch the included ones through the normal single-step actions
 * (`set_slot_time` / `set_slot_trainer` / `set_slot_capacity` / `set_slot_hidden`
 * / slot delete). Preview-then-confirm: nothing changes until the operator ticks
 * rows and confirms. `from`/`to` bound the candidate window (the current view's
 * fetch range), so the model can only touch what the operator can see.
 */
function BulkEditModal({
  from,
  to,
  onClose,
  onDone,
}: {
  from: string;
  to: string;
  onClose: () => void;
  onDone: () => void;
}): JSX.Element {
  const [text, setText] = useState("");
  const [parsing, setParsing] = useState(false);
  const [parseError, setParseError] = useState<string | null>(null);
  const [result, setResult] = useState<SlotBulkParseResponse | null>(null);
  const [included, setIncluded] = useState<ReadonlySet<number>>(new Set());
  // #13 — whether a retime of a BOOKED slot messages its members. Defaults ON:
  // a moved session the member doesn't hear about is the failure mode that
  // toggle exists to fix. Only rendered when an included row needs it.
  const [notify, setNotify] = useState(true);
  const [applying, setApplying] = useState(false);
  const [progress, setProgress] = useState<{ done: number; total: number } | null>(
    null,
  );
  const [applyError, setApplyError] = useState<string | null>(null);
  const [batchError, setBatchError] = useState<string | null>(null);
  const [outcomes, setOutcomes] = useState<ReadonlyArray<BulkApplyOutcome> | null>(
    null,
  );

  const parse = useCallback(async () => {
    setParsing(true);
    setParseError(null);
    setOutcomes(null);
    setBatchError(null);
    try {
      const r = await api.parseBulkSlotOps(text, from, to);
      setResult(r);
      // Every applicable row of a small rule starts ticked; the rows of a
      // large rule (`requires_ack`) wait for its "Include all N" — see
      // lib/bulk-apply.
      setIncluded(defaultIncluded(r.ops, r.rules));
      if (r.ops.length === 0) {
        setParseError(
          r.notes !== null
            ? r.notes
            : r.rules.length > 0
              ? "None of the rules matched a slot that can be changed — see each rule below."
              : "Couldn't match that to any slots in the current view. Try naming a day, time, or trainer.",
        );
      }
    } catch (e) {
      // Same rule as the availability modal: never leave a stale batch
      // applicable beneath the error.
      setResult(null);
      setIncluded(new Set());
      setParseError(describeParseFailure(e, "bulk"));
    } finally {
      setParsing(false);
    }
  }, [text, from, to]);

  const toggle = useCallback((i: number) => {
    setIncluded((prev) => {
      const next = new Set(prev);
      if (next.has(i)) next.delete(i);
      else next.add(i);
      return next;
    });
  }, []);

  /** "Include all N" on a `requires_ack` rule — ticks its applicable rows. */
  const includeAll = useCallback(
    (ruleIndex: number) => {
      setIncluded((prev) =>
        result === null ? prev : includeRule(prev, result.ops, ruleIndex),
      );
    },
    [result],
  );

  /**
   * Rows grouped under the rule that produced them, in rule order. A row whose
   * `rule_index` names no rule (never expected) still renders, under "Other",
   * rather than vanishing from a preview the operator is about to confirm.
   */
  const groups = useMemo((): ReadonlyArray<{
    readonly rule: BulkRuleSummary | null;
    readonly rows: ReadonlyArray<{ readonly op: BulkSlotOp; readonly i: number }>;
  }> => {
    if (result === null) return [];
    const indexed = result.ops.map((op, i) => ({ op, i }));
    const known = new Set(result.rules.map((r) => r.index));
    const byRule = result.rules.map((rule) => ({
      rule,
      rows: indexed.filter((r) => r.op.rule_index === rule.index),
    }));
    const orphans = indexed.filter((r) => !known.has(r.op.rule_index));
    return orphans.length > 0 ? [...byRule, { rule: null, rows: orphans }] : byRule;
  }, [result]);

  const dispatchOne = useCallback(
    async (op: BulkSlotOp, notifyBooked: boolean): Promise<BulkApplyOutcome> => {
      const base = { slot_id: op.slot_id, summary: op.summary };
      try {
        if (op.op === "retime" && op.starts_at && op.ends_at) {
          const r = await dispatchAction("set_slot_time", {
            slot_id: op.slot_id,
            starts_at: op.starts_at,
            ends_at: op.ends_at,
            // #13 — the action's gate reads an explicit `true`; absence keeps
            // the silent retime. Only a BOOKED slot has anyone to tell.
            ...(notifyBooked && op.current.taken_count > 0
              ? { notify_member: true }
              : {}),
          });
          return { ...base, ok: r.ok, ...(r.ok ? {} : { error: r.message || r.code }) };
        }
        if (op.op === "change_trainer" && op.trainer_id) {
          const r = await dispatchAction("set_slot_trainer", {
            slot_id: op.slot_id,
            trainer_id: op.trainer_id,
          });
          return { ...base, ok: r.ok, ...(r.ok ? {} : { error: r.message || r.code }) };
        }
        if (op.op === "change_capacity" && op.capacity !== undefined) {
          const r = await dispatchAction("set_slot_capacity", {
            slot_id: op.slot_id,
            capacity: op.capacity,
          });
          return { ...base, ok: r.ok, ...(r.ok ? {} : { error: r.message || r.code }) };
        }
        if (op.op === "hide" || op.op === "unhide") {
          const r = await dispatchAction("set_slot_hidden", {
            slot_id: op.slot_id,
            hidden: op.op === "hide",
          });
          return { ...base, ok: r.ok, ...(r.ok ? {} : { error: r.message || r.code }) };
        }
        // `delete` never reaches here — deletes go through the ONE batch call
        // in `confirm` (lib/bulk-apply.runBatchDelete), never a per-row
        // dispatch and never a cascade.
        return { ...base, ok: false, error: "unsupported_op" };
      } catch (e) {
        return { ...base, ok: false, error: (e as Error).message };
      }
    },
    [],
  );

  const confirm = useCallback(async () => {
    if (result === null) return;
    const plan = planBulkApply(result.ops, included);
    const total = plan.deletes.length + plan.edits.length;
    if (total === 0) return;
    setApplying(true);
    setApplyError(null);
    setBatchError(null);
    setProgress({ done: 0, total });
    let done = 0;
    // Deletes first, as ONE all-or-nothing `POST /api/data-row/slots` batch:
    // one transaction, one audit row. The parser already blocks a booked
    // slot's delete row; if one still reaches the server it rejects the whole
    // batch naming the row (`blockedId`) and the bounded loop resubmits the
    // rest. Until 2026-09-02 this modal cascade-deleted booked slots —
    // cancelling their bookings unannounced — and filed one audit row per
    // slot. Never again: no cascade, no per-row fallback.
    const batch = await runBatchDelete(plan.deletes, (ids) =>
      dataRowDeleteMany("slots", ids, "delete"),
    );
    done += plan.deletes.length;
    setProgress({ done, total });
    // Edits stay sequential — each is a distinct slot write through the
    // chokepoint; a serial loop keeps ordering clear and avoids hammering it.
    const editResults: BulkApplyOutcome[] = [];
    for (const op of plan.edits) {
      editResults.push(await dispatchOne(op, notify));
      done += 1;
      setProgress({ done, total });
    }
    // One ✓/✗ line per applied row, in preview order. `plan.edits` keeps op
    // order under the same predicate, so a cursor re-interleaves them.
    const results: BulkApplyOutcome[] = [];
    let editCursor = 0;
    result.ops.forEach((op, i) => {
      if (!included.has(i) || op.blocked !== null) return;
      if (op.op === "delete") {
        const o = batch.outcomes.get(op.slot_id);
        results.push({
          slot_id: op.slot_id,
          summary: op.summary,
          ok: o?.ok === true,
          ...(o === undefined
            ? { error: "not_attempted" }
            : o.error !== undefined
              ? { error: o.error }
              : {}),
        });
        return;
      }
      const o = editResults[editCursor];
      editCursor += 1;
      if (o !== undefined) results.push(o);
    });
    setOutcomes(results);
    setBatchError(batch.batchError);
    setProgress(null);
    setApplying(false);
    if (results.length > 0 && results.every((r) => r.ok)) {
      onDone();
    } else if (results.some((r) => !r.ok)) {
      setApplyError("Some operations didn't apply — see below.");
      onDone();
    }
  }, [result, included, notify, dispatchOne, onDone]);

  const applicable = result?.ops.filter((op) => op.blocked === null).length ?? 0;
  const includedCount = included.size;

  return (
    <div className="cal-modal-backdrop" onClick={onClose}>
      <div className="cal-modal cal-bulk-modal" onClick={(e) => e.stopPropagation()}>
        <div className="cal-modal-head">
          <h3>Bulk edit / delete</h3>
          <button className="cal-modal-x" onClick={onClose} aria-label="Close">
            ×
          </button>
        </div>
        <div className="cal-modal-body">
          {outcomes !== null ? (
            <div className="cal-bulk-done">
              <h4 className="cal-modal-sub">
                Applied {outcomes.filter((o) => o.ok).length}/{outcomes.length}
              </h4>
              <ul className="cal-bulk-list">
                {outcomes.map((o, i) => (
                  <li key={i} className={o.ok ? "cal-bulk-ok" : "cal-bulk-fail"}>
                    {o.ok ? "✓" : "✗"} {o.summary}
                    {o.ok ? "" : ` — ${o.error}`}
                  </li>
                ))}
              </ul>
              {batchError !== null ? (
                <p className="cal-del-error cal-action-error">{batchError}</p>
              ) : null}
              {applyError !== null ? (
                <p className="cal-del-error cal-action-error">{applyError}</p>
              ) : null}
              <div className="cal-del-actions">
                <button className="cal-book-confirm" onClick={onClose}>
                  Done
                </button>
              </div>
            </div>
          ) : (
            <>
              <p className="cal-modal-empty">
                Describe the change in plain English — e.g.{" "}
                <em>
                  "move all of Friday afternoon to next Monday", "give Nathalia's
                  Tuesday slots capacity 3", or "delete every empty slot next
                  week."
                </em>{" "}
                You'll see exactly what it will do before anything changes.
              </p>
              <textarea
                className="cal-avail-text"
                rows={4}
                maxLength={4000}
                placeholder="Move Tuesday 7am and 8am to Wednesday same times…"
                value={text}
                onChange={(e) => setText(e.target.value)}
              />
              <div className="cal-del-actions">
                <button
                  className="cal-book-confirm"
                  disabled={parsing || text.trim().length === 0}
                  onClick={() => void parse()}
                >
                  {parsing ? "Reading…" : result === null ? "Preview" : "Re-preview"}
                </button>
              </div>
              {parseError !== null ? (
                <p className="cal-del-error cal-action-error">{parseError}</p>
              ) : null}

              {result !== null &&
              (result.ops.length > 0 || result.rules.length > 0) ? (
                <div className="cal-bulk-preview">
                  <h4 className="cal-modal-sub">
                    {applicable} change{applicable === 1 ? "" : "s"} to apply
                    {result.ops.length > applicable
                      ? ` (${result.ops.length - applicable} blocked)`
                      : ""}
                  </h4>
                  <div className="cal-bulk-rules">
                    {groups.map((g, gi) => {
                      const rule = g.rule;
                      const blockedRows = g.rows.filter(
                        (r) => r.op.blocked !== null,
                      ).length;
                      const applicableRows = g.rows.filter(
                        (r) => r.op.blocked === null,
                      );
                      const unticked = applicableRows.filter(
                        (r) => !included.has(r.i),
                      ).length;
                      return (
                        <section
                          key={rule !== null ? `rule-${rule.index}` : `other-${gi}`}
                          className="cal-bulk-rule"
                        >
                          <div className="cal-bulk-rule-head">
                            {rule !== null ? (
                              <>
                                <span className="cal-bulk-rule-title">
                                  Rule {rule.index + 1} · {rule.summary}
                                </span>
                                <span className="cal-bulk-rule-meta">
                                  → {rule.matched} slot{rule.matched === 1 ? "" : "s"},{" "}
                                  {rule.applicable} to apply
                                  {blockedRows > 0 ? `, ${blockedRows} blocked` : ""}
                                </span>
                                {rule.requires_ack && unticked > 0 ? (
                                  <button
                                    type="button"
                                    className="cal-bulk-ack"
                                    onClick={() => includeAll(rule.index)}
                                  >
                                    Include all {applicableRows.length}
                                  </button>
                                ) : null}
                              </>
                            ) : (
                              <span className="cal-bulk-rule-title">Other</span>
                            )}
                          </div>
                          {rule !== null && rule.blocked_reason !== null ? (
                            <p className="cal-bulk-block">⛔ {rule.blocked_reason}</p>
                          ) : null}
                          {rule !== null
                            ? rule.warnings.map((w, wi) => (
                                <p key={wi} className="cal-bulk-warn">
                                  ⚠ {w}
                                </p>
                              ))
                            : null}
                          {g.rows.length > 0 ? (
                            <ul className="cal-bulk-list">
                              {g.rows.map(({ op, i }) => (
                                <li
                                  key={`${op.slot_id}-${i}`}
                                  className={`cal-bulk-op${op.blocked !== null ? " cal-bulk-blocked" : ""}`}
                                >
                                  <label className="cal-bulk-op-row">
                                    <input
                                      type="checkbox"
                                      disabled={op.blocked !== null}
                                      checked={op.blocked === null && included.has(i)}
                                      onChange={() => toggle(i)}
                                    />
                                    <span
                                      className={`cal-bulk-badge cal-bulk-badge-${op.op}`}
                                    >
                                      {op.op.replace("_", " ")}
                                    </span>
                                    <span className="cal-bulk-when">
                                      {op.current.local_label}
                                    </span>
                                    <span className="cal-bulk-summary">{op.summary}</span>
                                  </label>
                                  {op.warnings.map((w, wi) => (
                                    <p key={wi} className="cal-bulk-warn">
                                      ⚠ {w}
                                    </p>
                                  ))}
                                  {op.blocked !== null ? (
                                    <p className="cal-bulk-block">⛔ {op.blocked}</p>
                                  ) : null}
                                </li>
                              ))}
                            </ul>
                          ) : null}
                        </section>
                      );
                    })}
                  </div>
                  {result.notes !== null ? (
                    <p className="cal-avail-notes">Note: {result.notes}</p>
                  ) : null}
                  {needsNotifyChoice(result.ops, included) ? (
                    <label className="cal-bulk-notify">
                      <input
                        type="checkbox"
                        checked={notify}
                        disabled={applying}
                        onChange={(e) => setNotify(e.target.checked)}
                      />
                      Notify booked members of the new time
                    </label>
                  ) : null}
                  <div className="cal-del-actions">
                    <button
                      className="cal-book-confirm"
                      disabled={applying || includedCount === 0}
                      onClick={() => void confirm()}
                    >
                      {applying
                        ? progressLabel(
                            progress?.done ?? 0,
                            progress?.total ?? includedCount,
                          )
                        : `Apply ${includedCount} change${includedCount === 1 ? "" : "s"}`}
                    </button>
                  </div>
                  {applyError !== null ? (
                    <p className="cal-del-error cal-action-error">{applyError}</p>
                  ) : null}
                </div>
              ) : null}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
/**
 * #13 — retime-notify confirmation. When a BOOKED slot is dragged to a new
 * time, the operator confirms here and chooses whether to message the affected
 * members. Notify defaults ON (a moved session the member doesn't hear about is
 * the failure mode this feature exists to fix); unchecking sends a silent
 * retime (`notify_member` omitted). Delivery still depends on the studio flow
 * toggle being on — this only decides intent.
 */
function RetimeNotifyModal({
  slot,
  startIso,
  endIso,
  onCancel,
  onConfirm,
}: {
  slot: CalendarSlot;
  startIso: string;
  endIso: string;
  onCancel: () => void;
  onConfirm: (notify: boolean) => void;
}): JSX.Element {
  const [notify, setNotify] = useState(true);
  const [busy, setBusy] = useState(false);
  const count = slot.taken_count;
  return (
    <div className="cal-modal-backdrop" onClick={onCancel}>
      <div className="cal-modal" onClick={(e) => e.stopPropagation()}>
        <div className="cal-modal-head">
          <h3>Reschedule {count} booked session{count === 1 ? "" : "s"}</h3>
          <button className="cal-modal-x" onClick={onCancel} aria-label="Close">
            ×
          </button>
        </div>
        <div className="cal-modal-body">
          <p className="cal-retime-line">
            Moving to <strong>{fmtTime(startIso)}–{fmtTime(endIso)}</strong> on{" "}
            <strong>{fmtFullDay(new Date(startIso))}</strong>.
          </p>
          <label className="cal-retime-notify">
            <input
              type="checkbox"
              checked={notify}
              onChange={(e) => setNotify(e.target.checked)}
            />{" "}
            Notify the affected member{count === 1 ? "" : "s"} of the new time
          </label>
          <p className="cal-retime-hint">
            {notify
              ? "Each member gets a message with the updated time (if the studio's reschedule notice is switched on)."
              : "No message is sent — a silent time change."}
          </p>
        </div>
        <div className="cal-modal-foot">
          <button className="cal-cancel-keep" disabled={busy} onClick={onCancel}>
            Keep original time
          </button>
          <button
            className="cal-cancel-return"
            disabled={busy}
            onClick={() => {
              setBusy(true);
              onConfirm(notify);
            }}
          >
            {busy ? "Rescheduling…" : notify ? "Reschedule & notify" : "Reschedule silently"}
          </button>
        </div>
      </div>
    </div>
  );
}

/**
 * Late-drag credit prompt (Modal 1). A BOOKED slot dragged INSIDE the studio's
 * cancellation window is a "late change": per policy the member forfeits a
 * credit unless staff waives it. Staff pick here — the same Return/Use-credit
 * choice as the late-cancel flow — BEFORE the notify modal (Modal 2) + the move.
 * "Use credit" → forfeit 1 credit per affected member (the penalty, parity with
 * a late cancel + rebook); "Return credit" → a free move, no penalty.
 */
function LateDragCreditModal({
  slot,
  onCancel,
  onChoose,
}: {
  slot: CalendarSlot;
  onCancel: () => void;
  onChoose: (creditHandling: "consume" | "return") => void;
}): JSX.Element {
  const count = slot.taken_count;
  const hours = cancellationDeadlineHours();
  return (
    <div className="cal-modal-backdrop" onClick={onCancel}>
      <div className="cal-modal" onClick={(e) => e.stopPropagation()}>
        <div className="cal-modal-head">
          <h3>
            Late change — {count} booked session{count === 1 ? "" : "s"}
          </h3>
          <button className="cal-modal-x" onClick={onCancel} aria-label="Close">
            ×
          </button>
        </div>
        <div className="cal-modal-body">
          <p className="cal-retime-line">
            This session starts at <strong>{fmtTime(slot.starts_at)}</strong> on{" "}
            <strong>{fmtFullDay(new Date(slot.starts_at))}</strong> — within the
            studio&apos;s <strong>{hours}h cancellation window</strong> — so moving
            it is a late change. Choose how the credit is handled — you&apos;ll pick
            whether to notify the member next.
          </p>
          <p className="cal-retime-hint">
            <strong>Use credit</strong> — the member forfeits 1 session credit for
            the late change (they keep the session at the new time; anyone with no
            spare credit is moved anyway and flagged). <strong>Return credit</strong>{" "}
            — a free move, no penalty.
          </p>
        </div>
        <div className="cal-modal-foot">
          <button className="cal-cancel-keep" onClick={onCancel}>
            Keep original time
          </button>
          <button
            className="cal-cancel-return"
            title="Free move — no credit penalty"
            onClick={() => onChoose("return")}
          >
            Return credit
          </button>
          <button
            className="cal-cancel-consume"
            title="Late-change penalty — forfeit 1 credit per member"
            onClick={() => onChoose("consume")}
          >
            Use credit
          </button>
        </div>
      </div>
    </div>
  );
}

/**
 * "Add slot" tool — open a single bookable slot via the `staff_create_slot`
 * canonical action (trainer + start + duration + capacity + location). For a
 * recurring weekly schedule, "Add availability" (the LLM-parse paste tool) is
 * the right surface; this is the one-off counterpart. The action validates
 * trainer-in-studio + `starts_at < ends_at` at the dispatch chokepoint.
 */
interface TrainerOption {
  readonly id: string;
  readonly display_name: string;
}

function SlotCreateModal({
  defaultDate,
  defaultStartLocal,
  onClose,
  onSaved,
}: {
  defaultDate: Date;
  /**
   * Pre-filled `datetime-local` value (`YYYY-MM-DDTHH:mm`, STUDIO wall clock).
   * Set when the modal was opened by double-clicking empty calendar space, so
   * the form lands on the time that was clicked rather than the 09:00 default.
   */
  defaultStartLocal?: string;
  onClose: () => void;
  onSaved: () => void;
}): JSX.Element {
  const [trainers, setTrainers] = useState<ReadonlyArray<TrainerOption> | null>(
    null,
  );
  const [trainerId, setTrainerId] = useState("");
  // `startLocal` is a STUDIO wall-clock string (`datetime-local` has no zone);
  // it's converted to a UTC instant via `zonedDatetimeInputToIso` on submit,
  // so "09:00" means 9am at the studio no matter where the operator is.
  const [startLocal, setStartLocal] = useState(
    () => defaultStartLocal ?? `${localDateKey(defaultDate)}T09:00`,
  );
  const [durationMin, setDurationMin] = useState(45);
  const [capacity, setCapacity] = useState(1);
  const [location, setLocation] = useState<"studio" | "home">("studio");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Load the active trainer roster for the dropdown.
  useEffect(() => {
    let alive = true;
    void (async () => {
      const res = await dispatchAction<
        { status: string; limit: number },
        { trainers: ReadonlyArray<TrainerOption> }
      >("list_trainers", { status: "active", limit: 200 });
      if (!alive) return;
      if (res.ok) {
        setTrainers(res.response.trainers);
        setTrainerId(res.response.trainers[0]?.id ?? "");
      } else {
        setError(`Couldn't load trainers: ${res.message || res.code}`);
        setTrainers([]);
      }
    })();
    return () => {
      alive = false;
    };
  }, []);

  const create = useCallback(async () => {
    setError(null);
    if (trainerId === "") {
      setError("Pick a trainer.");
      return;
    }
    const startsAtIso = zonedDatetimeInputToIso(startLocal);
    const startMs = Date.parse(startsAtIso);
    if (!Number.isFinite(startMs)) {
      setError("Enter a valid start date & time.");
      return;
    }
    if (!Number.isInteger(capacity) || capacity < 1 || capacity > 50) {
      setError("Capacity must be a whole number between 1 and 50.");
      return;
    }
    const end = new Date(startMs + durationMin * 60_000);
    setSaving(true);
    try {
      const res = await dispatchAction<
        {
          trainer_id: string;
          starts_at: string;
          ends_at: string;
          capacity: number;
          location: "studio" | "home";
        },
        { slot_id: string }
      >("staff_create_slot", {
        trainer_id: trainerId,
        starts_at: startsAtIso,
        ends_at: end.toISOString(),
        capacity,
        location,
      });
      if (res.ok) {
        // Straight out on success. A "✓ Slot created / Done" step made the
        // operator dismiss a dialog to be told what the calendar behind it
        // already shows — the new slot is right there, drawn by `onSaved`.
        onSaved();
        onClose();
        return;
      }
      setError(`Couldn't create slot: ${res.message || res.code}`);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setSaving(false);
    }
  }, [
    trainerId,
    startLocal,
    durationMin,
    capacity,
    location,
    onSaved,
    onClose,
  ]);

  return (
    <div className="cal-modal-backdrop" onClick={onClose}>
      <div className="cal-modal" onClick={(e) => e.stopPropagation()}>
        <div className="cal-modal-head">
          <h3>Add slot</h3>
          <button className="cal-modal-x" onClick={onClose} aria-label="Close">
            ×
          </button>
        </div>
        <div className="cal-modal-body">
            <div className="cal-slotform">
              <label className="cal-slotform-row">
                <span>Trainer</span>
                <select
                  value={trainerId}
                  onChange={(e) => setTrainerId(e.target.value)}
                  disabled={trainers === null}
                >
                  {trainers === null ? (
                    <option value="">Loading…</option>
                  ) : trainers.length === 0 ? (
                    <option value="">No active trainers</option>
                  ) : (
                    trainers.map((t) => (
                      <option key={t.id} value={t.id}>
                        {t.display_name}
                      </option>
                    ))
                  )}
                </select>
              </label>
              <label className="cal-slotform-row">
                <span>Starts</span>
                <input
                  type="datetime-local"
                  value={startLocal}
                  onChange={(e) => setStartLocal(e.target.value)}
                />
              </label>
              <label className="cal-slotform-row">
                <span>Duration</span>
                <select
                  value={durationMin}
                  onChange={(e) => setDurationMin(Number(e.target.value))}
                >
                  <option value={30}>30 min</option>
                  <option value={45}>45 min</option>
                  <option value={60}>60 min</option>
                  <option value={90}>90 min</option>
                </select>
              </label>
              <label className="cal-slotform-row">
                <span>Capacity</span>
                <input
                  type="number"
                  min={1}
                  max={50}
                  value={capacity}
                  onChange={(e) => setCapacity(Number(e.target.value))}
                />
              </label>
              <label className="cal-slotform-row">
                <span>Location</span>
                <select
                  value={location}
                  onChange={(e) =>
                    setLocation(e.target.value as "studio" | "home")
                  }
                >
                  <option value="studio">Studio</option>
                  <option value="home">At-home</option>
                </select>
              </label>
            </div>
            <div className="cal-del-actions">
              <button
                className="cal-book-confirm"
                disabled={saving || trainerId === ""}
                onClick={() => void create()}
              >
                {saving ? "Creating…" : "Create slot"}
              </button>
            </div>
            {error !== null ? (
              <p className="cal-del-error cal-action-error">{error}</p>
            ) : null}
        </div>
      </div>
    </div>
  );
}

// ===========================================================================
// Slice 8 — Google-Calendar-style time grid + drag-to-reschedule (Week + Today).
// A vertical time axis with absolutely-positioned slot blocks; dragging a block
// up/down (5-min snap) — and across day columns in Week — dispatches
// `set_slot_time`. Month keeps its chip grid.
// ===========================================================================

const DEFAULT_WIN_START = 7 * 60; // 07:00
const DEFAULT_WIN_END = 21 * 60; // 21:00





const VIEW_LABELS: Record<CalView, string> = {
  today: "Today",
  week: "Week",
  month: "Month",
};

interface CalFilterOption {
  readonly value: string;
  readonly label: string;
}

/**
 * The toolbar's four icons. Hand-rolled inline SVG on purpose: there is no
 * icon library anywhere in this monorepo, and pulling one in for four glyphs
 * would be dependency creep the CHANGELOG would have to justify (invariant
 * 2). Shape copied from the one SVG that already existed —
 * `ConversationView.tsx`'s `SearchIcon` — so they share a stroke weight and a
 * box: 16×16, `viewBox="0 0 16 16"`, `stroke="currentColor"` so each inherits
 * its plate's colour (including the darker active state) with no extra rule.
 *
 * Explicit `width`/`height` matter: an `<svg>` with only a viewBox computes
 * `width: auto` against a shrink-to-fit grid item and resolves to the CSS
 * default 300×150, which blows straight out of the 32px phone plate.
 */
function CalIcon({
  children,
}: {
  readonly children: ReactNode;
}): JSX.Element {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 16 16"
      aria-hidden="true"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      {children}
    </svg>
  );
}

/** Map pin — the location filter (studio vs home). */
function LocationIcon(): JSX.Element {
  return (
    <CalIcon>
      <path d="M8 14.5s4.6-4.4 4.6-7.7a4.6 4.6 0 1 0-9.2 0C3.4 10.1 8 14.5 8 14.5Z" />
      <circle cx="8" cy="6.6" r="1.7" />
    </CalIcon>
  );
}

/** Head and shoulders — the trainer filter. */
function TrainerIcon(): JSX.Element {
  return (
    <CalIcon>
      <circle cx="8" cy="5.1" r="2.6" />
      <path d="M2.9 13.8a5.1 5.1 0 0 1 10.2 0" />
    </CalIcon>
  );
}

/** Tick — the booked-only filter. */
function BookedIcon(): JSX.Element {
  return (
    <CalIcon>
      <path d="M2.6 8.4 6.1 12l7.3-8" />
    </CalIcon>
  );
}

/** Plus — the add/edit tools menu. */
function PlusIcon(): JSX.Element {
  return (
    <CalIcon>
      <path d="M8 3.4v9.2M3.4 8h9.2" />
    </CalIcon>
  );
}

/**
 * The toolbar's filter control — the shared `<PopMenu>` wearing the toolbar's
 * button skin, showing the live value on its face. A native `<select>` was the
 * obvious choice and the wrong one; see `PopMenu`'s docstring for why its popup
 * could not be styled.
 */
function CalFilterMenu({
  label,
  icon,
  value,
  options,
  onChange,
}: {
  readonly label: string;
  /**
   * The filter's icon. Shown BESIDE the text everywhere, and INSTEAD of it on
   * phones — where "All locations ▾" and "All trainers ▾" ate the width the
   * filters and the add menu have to share. Both spellings are always in the
   * DOM and the breakpoint picks whether the text is one of them, so there is
   * still no JS width state to keep in sync with the CSS that decides.
   *
   * `ReactNode` rather than `string` since 2026-08-20: these were the single
   * letters `L` and `T`, which said very little for the space they took. They
   * are inline SVG now (there is no icon library in the monorepo and one is
   * not worth the dependency — invariant 2).
   */
  readonly icon: ReactNode;
  readonly value: string;
  readonly options: ReadonlyArray<CalFilterOption>;
  readonly onChange: (value: string) => void;
}): JSX.Element {
  const current = options.find((o) => o.value === value) ?? options[0];
  // A filter that is doing something gets a darker plate, since the icon alone
  // can't say "All locations" vs "Studio" the way the text spelling did.
  const active = current !== undefined && current.value !== options[0]?.value;
  return (
    <PopMenu
      label={label}
      triggerClassName={`cal-today-btn cal-filter-btn${active ? " cal-filter-btn-active" : ""}`}
      trigger={
        <>
          <span className="cal-filter-icon" aria-hidden="true">
            {icon}
          </span>
          <span className="cal-filter-text">
            {current?.label ?? ""} <span aria-hidden="true">▾</span>
          </span>
        </>
      }
    >
      {(close) =>
        options.map((o) => (
          <button
            key={o.value}
            type="button"
            className={o.value === value ? "pop-menu-opt-active" : undefined}
            onClick={() => {
              onChange(o.value);
              close();
            }}
          >
            {o.label}
          </button>
        ))
      }
    </PopMenu>
  );
}

export function Calendar(): JSX.Element {
  // The active layout (sticky across visits) + the focal day it's anchored on.
  const [searchParams, setSearchParams] = useSearchParams();
  const [view, setView] = useState<CalView>(() =>
    coerceMobileView(parseViewParam(searchParams.get("view")) ?? loadStoredView()),
  );
  // A `?view=` param (e.g. the mobile home screen's `/calendar?view=today`)
  // wins over the stored preference and is persisted, so a subsequent
  // no-param visit (the Calendar tab) keeps showing it.
  useEffect(() => {
    const fromParam = parseViewParam(searchParams.get("view"));
    if (!fromParam) return;
    const next = coerceMobileView(fromParam);
    setView(next);
    try {
      localStorage.setItem(VIEW_STORAGE_KEY, next);
    } catch {
      // Persistence is best-effort.
    }
  }, [searchParams]);

  // C3 — `/calendar?slot=<id>&date=<yyyy-mm-dd>` deep link (the member page's
  // "Calendar" row action). `date` anchors the view, because the loaded slot
  // range is view-scoped: without it, a session outside the current week simply
  // isn't in `slots` and there would be nothing to select.
  const [anchor, setAnchor] = useState<Date>(
    () => parseCalendarDeepLinkDate(searchParams.get("date")) ?? studioToday(),
  );
  const [slots, setSlots] = useState<ReadonlyArray<CalendarSlot>>([]);
  const [loading, setLoading] = useState(true);
  const [loadCount, setLoadCount] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [selected, setSelected] = useState<CalendarSlot | null>(null);
  const deepLinkSlotId = searchParams.get("slot");
  const deepLinkDone = useRef<string | null>(null);
  const [availOpen, setAvailOpen] = useState(false);
  const [slotCreateOpen, setSlotCreateOpen] = useState(false);
  /**
   * Studio wall-clock `datetime-local` the Add-slot form should open on, set
   * when the form was summoned by double-clicking empty grid. NULL means it
   * was opened from the toolbar, where the existing 09:00 default is right.
   */
  const [createAtLocal, setCreateAtLocal] = useState<string | null>(null);
  const openCreateAt = useCallback((startLocal: string): void => {
    setCreateAtLocal(startLocal);
    setSlotCreateOpen(true);
  }, []);
  /** First tap of a candidate double-tap on a MONTH cell (touch only). */
  const monthTap = useRef<TapCandidate | null>(null);
  const [bulkOpen, setBulkOpen] = useState(false);
  // #13 — a booked-slot drag-retime parks here so the operator can decide
  // whether to message the affected members before the `set_slot_time` write
  // lands. `notify` defaults ON (the member SHOULD hear their session moved);
  // unchecking it threads `notify_member:false` → silent retime.
  const [retimeConfirm, setRetimeConfirm] = useState<{
    slot: CalendarSlot;
    startIso: string;
    endIso: string;
    // Late-drag penalty carried from the credit modal (Modal 1). `'consume'` →
    // forfeit 1 credit per affected member after the move; `'return'` → free
    // move. Undefined for a normal (outside-window) retime.
    creditHandling?: "consume" | "return";
  } | null>(null);

  // Late-drag credit prompt (Modal 1). A booked slot dragged INSIDE the studio's
  // cancellation window parks here first so the operator picks Use/Return credit
  // before the notify modal (Modal 2) + the move land.
  const [lateCredit, setLateCredit] = useState<{
    slot: CalendarSlot;
    startIso: string;
    endIso: string;
  } | null>(null);

  // Slice 10 — sticky client-side Location + Trainer filters. Narrow the loaded
  // window without a re-fetch (the calendar API takes only from/to), so the
  // grid, counts + drag all operate on the filtered set.
  const [locFilter, setLocFilter] = useState<LocFilter>(() =>
    loadStoredLocFilter(),
  );
  const [trainerFilter, setTrainerFilter] = useState<string>(() =>
    loadStoredTrainerFilter(),
  );
  const [bookedFilter, setBookedFilter] = useState<BookedFilter>(() =>
    loadStoredBookedFilter(),
  );
  const [trainerFilterOptions, setTrainerFilterOptions] = useState<
    ReadonlyArray<{ value: string; label: string }>
  >([]);

  // C3 — re-anchor when `?date=` changes after mount. The `useState` seed above
  // covers arriving from the member page (a fresh mount); this covers a
  // back-nav to a deep link while the calendar is already open, where the
  // component is reused and the seed never re-runs.
  useEffect(() => {
    const d = parseCalendarDeepLinkDate(searchParams.get("date"));
    if (d === null) return;
    setAnchor(d);
  }, [searchParams]);

  // The fetch window + the days the chosen view renders. Month = the 6-week
  // grid (Monday on/before the 1st, 42 days); week = Mon–Sun of the anchor's
  // week; today = the single anchor day.
  const period = useMemo(() => {
    if (view === "month") {
      const monthFirst = new Date(anchor.getFullYear(), anchor.getMonth(), 1);
      const start = startOfWeekMonday(monthFirst);
      const days: Date[] = [];
      for (let i = 0; i < 42; i += 1) days.push(addDays(start, i));
      return { rangeStart: start, rangeEnd: addDays(start, 42), days };
    }
    if (view === "week") {
      const start = startOfWeekMonday(anchor);
      const days: Date[] = [];
      for (let i = 0; i < 7; i += 1) days.push(addDays(start, i));
      return { rangeStart: start, rangeEnd: addDays(start, 7), days };
    }
    const start = startOfDay(anchor);
    return { rangeStart: start, rangeEnd: addDays(start, 1), days: [start] };
  }, [view, anchor]);

  const { rangeStart, rangeEnd } = period;

  const load = useCallback(async (): Promise<
    ReadonlyArray<CalendarSlot> | null
  > => {
    setLoading(true);
    setError(null);
    try {
      // Fetch bounds = STUDIO midnights of the rendered day range (the day
      // carriers' browser-local midnights can under/overshoot by the viewer's
      // offset, dropping edge slots from the last rendered day).
      const res = await api.calendar(
        zonedDatetimeInputToIso(`${localDateKey(rangeStart)}T00:00`),
        zonedDatetimeInputToIso(`${localDateKey(rangeEnd)}T00:00`),
      );
      setSlots(res.slots);
      // C3 — bumped only on SUCCESS. The deep-link effect below waits on this
      // rather than on `loading`, so it can never read the initial empty
      // `slots` as "that session is gone".
      setLoadCount((n) => n + 1);
      return res.slots;
    } catch (e) {
      setError((e as Error).message);
      return null;
    } finally {
      setLoading(false);
    }
  }, [rangeStart, rangeEnd]);

  useEffect(() => {
    void load();
  }, [load]);

  // C3 — open the deep-linked slot's popup once its range has loaded. One-shot
  // per slot id: re-opening on every reload would fight the operator closing it.
  useEffect(() => {
    const wanted = deepLinkSlotId;
    if (wanted === null || loadCount === 0) return;
    if (deepLinkDone.current === wanted) return;
    deepLinkDone.current = wanted;
    const hit = slots.find((s) => s.id === wanted);
    if (hit !== undefined) {
      setSelected(hit);
      return;
    }
    setError(
      "That session isn't on the calendar any more — it may have been deleted or moved.",
    );
  }, [deepLinkSlotId, slots, loadCount]);

  // Slice 10 — persist the filters + populate the trainer dropdown once.
  useEffect(() => {
    try {
      localStorage.setItem(LOC_FILTER_STORAGE_KEY, locFilter);
    } catch {
      // ignore — non-persistent is fine.
    }
  }, [locFilter]);
  useEffect(() => {
    try {
      localStorage.setItem(TRAINER_FILTER_STORAGE_KEY, trainerFilter);
    } catch {
      // ignore.
    }
  }, [trainerFilter]);
  useEffect(() => {
    try {
      localStorage.setItem(BOOKED_FILTER_STORAGE_KEY, bookedFilter);
    } catch {
      // ignore.
    }
  }, [bookedFilter]);
  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const res = await api.lookupOptions("trainers");
        if (!cancelled) {
          setTrainerFilterOptions(
            res.options.map((o) => ({ value: o.value, label: o.label })),
          );
        }
      } catch {
        // A failed trainer lookup just leaves the dropdown at "All trainers".
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // Slice 10 — the filtered working set every downstream view reads. Location
  // uses the same `slotLocation()` the colour-coding does; trainer is an exact
  // id match. An unknown-location slot is only hidden when a studio/home filter
  // is active.
  const filteredSlots = useMemo(() => {
    if (locFilter === "all" && trainerFilter === "" && bookedFilter === "all") {
      return slots;
    }
    return slots.filter((s) => {
      if (locFilter !== "all" && slotLocation(s) !== locFilter) return false;
      if (trainerFilter !== "" && s.trainer_id !== trainerFilter) return false;
      // `taken_count` EXCLUDES cancelled bookings, so a slot whose only
      // bookings were cancelled correctly reads as unbooked and disappears
      // under "Booked only". That is the intended answer, not a defect —
      // the slot really is free capacity again.
      if (bookedFilter === "booked" && s.taken_count <= 0) return false;
      return true;
    });
  }, [slots, locFilter, trainerFilter, bookedFilter]);

  // C3 — drop `?slot=&date=` once the popup is dismissed, so a back-nav or a
  // refresh doesn't re-open it. `?view=` is preserved: it is a real preference,
  // and the effect that reads it has already persisted it either way.
  const closeDeepLink = useCallback(() => {
    setSearchParams(
      (prev) => {
        if (!prev.has("slot") && !prev.has("date")) return prev;
        const next = new URLSearchParams(prev);
        next.delete("slot");
        next.delete("date");
        return next;
      },
      { replace: true },
    );
  }, [setSearchParams]);

  // After a book/cancel/delete, reload the range and re-point the open modal at
  // the refreshed slot — so the slot auto-refreshes in place (the booked member
  // appears / the cancelled one drops off). A deleted slot is gone from the
  // fresh list → modal closes.
  const reloadAndReselect = useCallback(
    async (slotId: string): Promise<void> => {
      const fresh = await load();
      if (fresh === null) return;
      setSelected(fresh.find((s) => s.id === slotId) ?? null);
    },
    [load],
  );

  // Bucket slots by their STUDIO calendar day (already time-sorted by the
  // API) — browser-local bucketing put late-evening slots on the wrong day
  // for any viewer not on studio time.
  const byDay = useMemo(() => {
    const map = new Map<string, CalendarSlot[]>();
    for (const s of filteredSlots) {
      const key = isoToZonedDateKey(s.starts_at);
      const arr = map.get(key);
      if (arr === undefined) map.set(key, [s]);
      else arr.push(s);
    }
    return map;
  }, [filteredSlots]);

  // Slice 8 — the visible time window for the Week/Today grid: the data's span
  // (padded to whole hours) clamped to a sane 07:00–21:00 fallback, so a quiet
  // day still looks normal and an early/late slot is never clipped.
  const timeWindow = useMemo(() => {
    let start = DEFAULT_WIN_START;
    let end = DEFAULT_WIN_END;
    for (const s of filteredSlots) {
      const st = slotStartMin(s.starts_at);
      const en = st + slotDurationMin(s);
      if (st < start) start = st;
      if (en > end) end = en;
    }
    start = Math.max(0, Math.floor(start / 60) * 60);
    end = Math.min(24 * 60, Math.ceil(end / 60) * 60);
    if (end <= start) end = start + 60;
    return { start, end };
  }, [filteredSlots]);

  // Slice 8 — drag-drop reschedule: dispatch `set_slot_time`. On failure surface
  // + reload to snap the block back to its true time. #13 — the write can now
  // notify the affected members (`notify_member`), so a booked slot no longer
  // fires a native confirm but parks in the retime modal where the operator
  // picks notify-or-silent. An EMPTY slot has no members → applies immediately,
  // silently.
  const applyRetime = useCallback(
    async (
      slot: CalendarSlot,
      startIso: string,
      endIso: string,
      notify: boolean,
      creditHandling?: "consume" | "return",
    ): Promise<void> => {
      const r = await dispatchAction("set_slot_time", {
        slot_id: slot.id,
        starts_at: startIso,
        ends_at: endIso,
        // Only thread the flag when notifying — the action's gate reads an
        // explicit `true`; absence keeps the pre-#13 silent behaviour.
        ...(notify ? { notify_member: true } : {}),
      });
      if (!r.ok) {
        setError(`Reschedule failed: ${r.message || r.code}`);
        await load();
        return;
      }
      // Late-change penalty: after a move INSIDE the cancellation window where
      // staff chose "Use credit", forfeit ONE credit per affected active
      // booking. "Return credit" (or a normal retime) forfeits nothing. A
      // member with no forfeitable credit is skipped + surfaced, never blocked.
      if (creditHandling === "consume") {
        const affected = slot.bookings.filter(
          (b) => b.status === "active" || b.status === "completed",
        );
        let forfeited = 0;
        let skipped = 0;
        for (const b of affected) {
          const fr = await dispatchAction<
            { booking_id: string },
            { forfeited?: boolean }
          >("forfeit_booking_credit", { booking_id: b.id });
          if (fr.ok) {
            if (fr.response?.forfeited === true) forfeited += 1;
            else skipped += 1;
          } else {
            skipped += 1;
          }
        }
        if (skipped > 0) {
          setError(
            `Session moved. Forfeited ${forfeited} credit${forfeited === 1 ? "" : "s"}; ` +
              `${skipped} member${skipped === 1 ? "" : "s"} had no credit available to forfeit.`,
          );
        }
      }
      await load();
    },
    [load],
  );

  const rescheduleSlot = useCallback(
    async (slot: CalendarSlot, startIso: string, endIso: string): Promise<void> => {
      if (slot.taken_count > 0) {
        // Booked. A drag whose CURRENT session start is inside the studio's
        // cancellation window is a "late change" → the credit-penalty prompt
        // (Modal 1) first, then the notify modal (Modal 2). Otherwise the plain
        // notify modal. The block visually holds its dropped position until the
        // modal(s) resolve + load() re-snaps.
        if (isWithinCancellationWindow(slot.starts_at)) {
          setLateCredit({ slot, startIso, endIso });
        } else {
          setRetimeConfirm({ slot, startIso, endIso });
        }
        return;
      }
      await applyRetime(slot, startIso, endIso, false);
    },
    [applyRetime],
  );

  const todayKey = isoToZonedDateKey(new Date().toISOString());
  const anchorIsToday = localDateKey(anchor) === todayKey;
  const focusMonth = anchor.getMonth();

  const step = (delta: number): void => {
    setAnchor((a) =>
      view === "month"
        ? addMonths(a, delta)
        : addDays(a, view === "week" ? delta * 7 : delta),
    );
  };
  const goToday = (): void => setAnchor(studioToday());
  const changeView = (v: CalView): void => {
    setView(v);
    try {
      localStorage.setItem(VIEW_STORAGE_KEY, v);
    } catch {
      // Persistence is best-effort.
    }
  };

  const label =
    view === "month"
      ? fmtMonthLabel(anchor)
      : view === "week"
        ? fmtWeekRange(rangeStart)
        : fmtFullDay(anchor);
  const unit = view === "month" ? "month" : view === "week" ? "week" : "day";

  return (
    <div className="cal-page">
      <div className="cal-toolbar">
        <div className="cal-toolbar-left">
          <div className="cal-viewswitch" role="tablist" aria-label="Calendar view">
            {(["today", "week", "month"] as CalView[]).map((v) => (
              <button
                key={v}
                role="tab"
                aria-selected={view === v}
                className={`cal-viewbtn cal-viewbtn-${v}${view === v ? " cal-viewbtn-active" : ""}`}
                onClick={() => changeView(v)}
              >
                {VIEW_LABELS[v]}
              </button>
            ))}
          </div>
        </div>
        {/* Its own zone so the date sits at the PAGE's centre, not at the
            centre of whatever space the two side groups happen to leave. */}
        <div className="cal-toolbar-center">
          <button onClick={() => step(-1)} aria-label={`Previous ${unit}`}>
            ‹
          </button>
          <h1 className="cal-month-label">{label}</h1>
          <button onClick={() => step(1)} aria-label={`Next ${unit}`}>
            ›
          </button>
        </div>
        <div className="cal-toolbar-right">
          <CalFilterMenu
            label="Filter by location"
            icon={<LocationIcon />}
            value={locFilter}
            options={[
              { value: "all", label: "All locations" },
              { value: "studio", label: "Studio" },
              { value: "home", label: "Home" },
            ]}
            onChange={(v) => setLocFilter(v as LocFilter)}
          />
          <CalFilterMenu
            label="Filter by trainer"
            icon={<TrainerIcon />}
            value={trainerFilter}
            options={[
              { value: "", label: "All trainers" },
              ...trainerFilterOptions,
            ]}
            onChange={setTrainerFilter}
          />
          <CalFilterMenu
            label="Filter by booked"
            icon={<BookedIcon />}
            value={bookedFilter}
            options={[
              { value: "all", label: "All sessions" },
              { value: "booked", label: "Booked only" },
            ]}
            onChange={(v) => setBookedFilter(v as BookedFilter)}
          />
          {/* The add/edit tools, at the right of the toolbar beside the
              filters, at every width. */}
          <PopMenu
            label="Add / edit tools"
            triggerClassName="cal-today-btn cal-actions-menu-btn"
            trigger={
              <span className="cal-filter-icon" aria-hidden="true">
                <PlusIcon />
              </span>
            }
          >
            {(close) => (
              <>
                <button
                  onClick={() => {
                    close();
                    setAvailOpen(true);
                  }}
                >
                  Add availability
                </button>
                <button
                  onClick={() => {
                    close();
                    setSlotCreateOpen(true);
                  }}
                >
                  Add slot
                </button>
                <button
                  onClick={() => {
                    close();
                    setBulkOpen(true);
                  }}
                >
                  Bulk edit / delete
                </button>
              </>
            )}
          </PopMenu>
        </div>
      </div>

      {error !== null ? (
        <div className="cal-error">Failed to load calendar: {error}</div>
      ) : null}

      {/* The ONE scroll container. `.cal-page` is a fixed-height flex column
          (toolbar pinned, this filling the rest), so the grid runs to the
          bottom edge of the viewport and scrolls inside itself rather than
          scrolling the whole page away from the toolbar. */}
      <div className="cal-body">
      {view === "month" ? (
        <div className="cal-grid">
          {WEEKDAY_LABELS.map((w) => (
            <div key={w} className="cal-grid-head">
              {w}
            </div>
          ))}
          {period.days.map((d) => {
            const key = localDateKey(d);
            const daySlots = byDay.get(key) ?? [];
            const outside = d.getMonth() !== focusMonth;
            const isToday = key === todayKey;
            return (
              <div
                key={key}
                className={`cal-cell${outside ? " cal-cell-outside" : ""}${
                  isToday ? " cal-cell-today" : ""
                }`}
                // Month cells have no time axis, so a double-click can only
                // carry the DAY — the form keeps its 09:00 default and the
                // operator sets the hour. Guarded to empty space so
                // double-clicking a slot chip stays "open that slot".
                onDoubleClick={(e) => {
                  if (
                    e.target instanceof Element &&
                    e.target.closest(".cal-slot") !== null
                  ) {
                    return;
                  }
                  openCreateAt(`${key}T09:00`);
                }}
                onPointerUp={(e) => {
                  if (e.pointerType === "mouse") return;
                  if (
                    e.target instanceof Element &&
                    e.target.closest(".cal-slot") !== null
                  ) {
                    return;
                  }
                  const tap: TapCandidate = {
                    t: e.timeStamp,
                    x: e.clientX,
                    y: e.clientY,
                    key,
                  };
                  if (isDoubleTap(monthTap.current, tap)) {
                    monthTap.current = null;
                    openCreateAt(`${key}T09:00`);
                    return;
                  }
                  monthTap.current = tap;
                }}
              >
                <div className="cal-cell-date">{d.getDate()}</div>
                <div className="cal-cell-slots">
                  {daySlots.map((s) => (
                    <button
                      key={s.id}
                      className={`cal-slot cal-tag-${slotFill(s)} cal-loc-${slotLocation(s)}${s.hidden ? " cal-hidden" : ""}`}
                      onClick={() => setSelected(s)}
                      title={slotChipTitle(s)}
                    >
                      <span className="cal-slot-time">
                        {fmtTime(s.starts_at)}
                      </span>
                      <span
                        className={`cal-slot-trainer${
                          slotChipShowsMember(s) ? " cal-slot-booked" : ""
                        }`}
                      >
                        {slotChipName(s)}
                      </span>
                      <span className="cal-slot-cap">
                        {s.taken_count}/{s.capacity}
                      </span>
                    </button>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      ) : null}

      {view === "week" ? (
        <CalendarTimeline
          days={period.days}
          slotsForDay={(key) => byDay.get(key) ?? []}
          winStart={timeWindow.start}
          winEnd={timeWindow.end}
          onOpen={setSelected}
          onReschedule={rescheduleSlot}
          onCreateAt={openCreateAt}
        />
      ) : null}

      {view === "today"
        ? (() => {
            const dayKey = localDateKey(anchor);
            const daySlots = byDay.get(dayKey) ?? [];
            const bookingCount = daySlots.reduce(
              (n, s) => n + s.bookings.length,
              0,
            );
            return (
              <div className="cal-today">
                <div className="cal-today-head">
                  <h2>
                    {fmtFullDay(anchor)}
                    {anchorIsToday ? (
                      <span className="cal-today-pill">Today</span>
                    ) : null}
                  </h2>
                  {!loading ? (
                    <span className="cal-today-sub">
                      {daySlots.length} slot{daySlots.length === 1 ? "" : "s"} ·{" "}
                      {bookingCount} booked
                    </span>
                  ) : null}
                </div>
                {daySlots.length === 0 ? (
                  <p className="cal-today-empty">
                    {loading ? "Loading…" : "No slots scheduled on this day."}
                  </p>
                ) : (
                  <CalendarTimeline
                    days={[anchor]}
                    slotsForDay={(key) => byDay.get(key) ?? []}
                    winStart={timeWindow.start}
                    winEnd={timeWindow.end}
                    onOpen={setSelected}
                    onReschedule={rescheduleSlot}
                    onCreateAt={openCreateAt}
                  />
                )}
              </div>
            );
          })()
        : null}
      </div>

      {selected !== null ? (
        <SlotModal
          slot={selected}
          onClose={() => {
            setSelected(null);
            closeDeepLink();
          }}
          onChanged={() =>
            // Auto-trigger the same refresh the top-right Refresh button does,
            // then re-point the modal at the refreshed slot. Returned (not
            // voided) so modal actions can await the re-point before clearing
            // their busy flags; load() catches its own errors, so this promise
            // always resolves.
            reloadAndReselect(selected.id)
          }
        />
      ) : null}

      {availOpen ? (
        <AvailabilityModal
          onClose={() => setAvailOpen(false)}
          onSaved={() => {
            // Newly generated slots appear on the grid.
            void load();
          }}
        />
      ) : null}

      {slotCreateOpen ? (
        <SlotCreateModal
          defaultDate={anchor}
          {...(createAtLocal !== null ? { defaultStartLocal: createAtLocal } : {})}
          onClose={() => {
            setSlotCreateOpen(false);
            setCreateAtLocal(null);
          }}
          onSaved={() => {
            // The new slot appears on the grid for the current window.
            void load();
          }}
        />
      ) : null}

      {bulkOpen ? (
        <BulkEditModal
          from={zonedDatetimeInputToIso(`${localDateKey(rangeStart)}T00:00`)}
          to={zonedDatetimeInputToIso(`${localDateKey(rangeEnd)}T00:00`)}
          onClose={() => setBulkOpen(false)}
          onDone={() => {
            // Applied ops change the grid — refresh the current window.
            void load();
          }}
        />
      ) : null}

      {lateCredit !== null ? (
        <LateDragCreditModal
          slot={lateCredit.slot}
          onCancel={() => {
            // Keep the original time — snap the dragged block back.
            setLateCredit(null);
            void load();
          }}
          onChoose={(creditHandling) => {
            const pending = lateCredit;
            setLateCredit(null);
            // Advance to the notify modal (Modal 2), carrying the credit choice.
            setRetimeConfirm({
              slot: pending.slot,
              startIso: pending.startIso,
              endIso: pending.endIso,
              creditHandling,
            });
          }}
        />
      ) : null}

      {retimeConfirm !== null ? (
        <RetimeNotifyModal
          slot={retimeConfirm.slot}
          startIso={retimeConfirm.startIso}
          endIso={retimeConfirm.endIso}
          onCancel={() => {
            // Snap the dragged block back to its true time.
            setRetimeConfirm(null);
            void load();
          }}
          onConfirm={(notify) => {
            const pending = retimeConfirm;
            setRetimeConfirm(null);
            void applyRetime(
              pending.slot,
              pending.startIso,
              pending.endIso,
              notify,
              pending.creditHandling,
            );
          }}
        />
      ) : null}
    </div>
  );
}
